import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { ExecutiveSummaryModal } from '../components/ExecutiveSummaryModal';
import { BRDModal } from '../components/BRDModal';
import { RequirementsReviewModal } from '../components/RequirementsReviewModal';
import { ProjectApprovalModal } from '../components/ProjectApprovalModal';
import * as projectService from '../services/projectService';
import * as stakeholderApprovalService from '../services/stakeholderApprovalService';
import * as projectTypeService from '../services/projectTypeService';
import * as techStackService from '../services/techStackService';
import * as userService from '../services/userService';
import * as requirementService from '../services/requirementService';
import * as personaService from '../services/personaService';
import * as milestoneService from '../services/milestoneService';
import type { Milestone } from '../services/milestoneService';
import RichTextEditor from '../components/RichTextEditor';
import RichTextDisplay from '../components/RichTextDisplay';
import type { Project, ProjectMember, UpdateProjectInput, AddMemberInput, AddStakeholderInput, ProjectTechStackItem, RequirementsReviewData } from '../services/projectService';
import type { ProjectType } from '../services/projectTypeService';
import type { TechStackItem } from '../services/techStackService';
import type { User } from '../services/userService';
import type { RequirementWithDetails } from '../services/requirementService';
import type { Persona, CreatePersonaInput } from '../services/personaService';
import type { ApprovalSummary } from '../services/stakeholderApprovalService';

// Top 50 most commonly used technology stacks (EXACT database names from seed file)
const TOP_TECH_STACKS = [
  // Frontend Frameworks
  'React',
  'Vue.js',
  'Angular',
  'Next.js',
  'Svelte',

  // Mobile
  'React Native',
  'Flutter',
  'Swift (iOS)',
  'Kotlin (Android)',

  // UI Libraries & CSS
  'Tailwind CSS',
  'Bootstrap',
  'Material-UI (MUI)',
  'SASS/SCSS',

  // State Management
  'Redux',
  'Context API',

  // Backend - Node.js
  'Express.js',
  'NestJS',

  // Backend - Python
  'Django',
  'Flask',
  'FastAPI',

  // Backend - Java & .NET
  'Spring Boot',
  'ASP.NET Core',
  '.NET Core',

  // Backend - Other
  'Ruby on Rails',
  'Laravel',
  'Go (Golang)',

  // API Protocols
  'REST',
  'GraphQL',
  'WebSocket',

  // Databases - Relational
  'PostgreSQL',
  'MySQL',
  'Microsoft SQL Server',
  'SQLite',

  // Databases - NoSQL
  'MongoDB',
  'Redis',
  'Firebase Firestore',

  // Cloud Platforms
  'Amazon Web Services (AWS)',
  'Microsoft Azure',
  'Google Cloud Platform (GCP)',
  'Heroku',
  'Vercel',

  // DevOps & CI/CD
  'Docker',
  'Kubernetes',
  'GitHub Actions',
  'GitLab CI/CD',
  'Jenkins',

  // Version Control
  'Git',
  'GitHub',
  'GitLab',

  // Project Management
  'Jira'
];

export function ProjectDetail(): React.ReactElement | null {
  const { id } = useParams<{ id: string }>();
  const { user: currentUser, company, token } = useAuth();
  const navigate = useNavigate();

  const [project, setProject] = useState<Project | null>(null);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [projectTypes, setProjectTypes] = useState<ProjectType[]>([]);
  const [currentProjectType, setCurrentProjectType] = useState<ProjectType | null>(null);
  const [projectTechStack, setProjectTechStack] = useState<ProjectTechStackItem[]>([]);
  const [availableTechStack, setAvailableTechStack] = useState<TechStackItem[]>([]);
  const [companyUsers, setCompanyUsers] = useState<User[]>([]);
  const [requirements, setRequirements] = useState<RequirementWithDetails[]>([]);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Edit modal
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState<UpdateProjectInput>({});
  const [editError, setEditError] = useState('');

  // Add type selection modal (Team Member or Stakeholder)
  const [showAddTypeSelection, setShowAddTypeSelection] = useState(false);

  // Add member modal
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [addMemberData, setAddMemberData] = useState<AddMemberInput>({ user_id: '', role: 'contributor' });
  const [addMemberError, setAddMemberError] = useState('');

  // Add stakeholder modal
  const [isAddingStakeholder, setIsAddingStakeholder] = useState(false);
  const [addStakeholderData, setAddStakeholderData] = useState<AddStakeholderInput>({ stakeholder_name: '', stakeholder_responsibility: '', role: 'stakeholder' });
  const [addStakeholderError, setAddStakeholderError] = useState('');

  // Edit stakeholder modal
  const [isEditingStakeholder, setIsEditingStakeholder] = useState(false);
  const [editStakeholderData, setEditStakeholderData] = useState({ stakeholder_name: '', stakeholder_responsibility: '' });
  const [editStakeholderMemberId, setEditStakeholderMemberId] = useState('');
  const [editStakeholderError, setEditStakeholderError] = useState('');

  // Add tech stack modal
  const [isAddingTechStack, setIsAddingTechStack] = useState(false);
  const [selectedTechStackId, setSelectedTechStackId] = useState('');
  const [addTechStackError, setAddTechStackError] = useState('');
  const [techStackSearch, setTechStackSearch] = useState('');

  // Preferences state - loaded from API and synced
  const [preferences, setPreferences] = useState<Record<string, boolean>>({});
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);

  // Helper function to get saved collapsed state (from preferences or localStorage fallback)
  const getSavedExpandedState = (key: string, defaultValue: boolean): boolean => {
    // If preferences are loaded from API, use them
    if (preferencesLoaded && preferences.hasOwnProperty(`projectDetail_${key}_expanded`)) {
      return preferences[`projectDetail_${key}_expanded`];
    }
    // Otherwise, fall back to localStorage for immediate feedback
    const saved = localStorage.getItem(`projectDetail_${key}_expanded`);
    return saved !== null ? saved === 'true' : defaultValue;
  };

  // Helper function to save collapsed state (to both localStorage and API)
  const saveExpandedState = (key: string, value: boolean) => {
    // Save to localStorage immediately for instant feedback
    localStorage.setItem(`projectDetail_${key}_expanded`, String(value));

    // Update preferences state
    const prefKey = `projectDetail_${key}_expanded`;
    setPreferences(prev => ({ ...prev, [prefKey]: value }));

    // Debounced API call will handle saving to database
    debouncedSavePreferences({ ...preferences, [prefKey]: value });
  };

  // Tech stack collapsible section
  const [isTechStackExpanded, setIsTechStackExpanded] = useState(() => getSavedExpandedState('techStack', false));

  // Project info collapsible section
  const [isProjectInfoExpanded, setIsProjectInfoExpanded] = useState(() => getSavedExpandedState('projectInfo', true));
  const [isInlineEditing, setIsInlineEditing] = useState(false);
  const [inlineEditData, setInlineEditData] = useState<UpdateProjectInput>({});

  // Scope section editing state (separate from project info)
  const [isEditingScope, setIsEditingScope] = useState(false);
  const [scopeEditData, setScopeEditData] = useState({ in_scope: '', out_scope: '' });

  // Requirements overview collapsible section
  const [isRequirementsExpanded, setIsRequirementsExpanded] = useState(() => getSavedExpandedState('requirements', true));

  // Team Members collapsible section
  const [isTeamMembersExpanded, setIsTeamMembersExpanded] = useState(() => getSavedExpandedState('teamMembers', true));

  // Personas collapsible section
  const [isPersonasExpanded, setIsPersonasExpanded] = useState(() => getSavedExpandedState('personas', true));

  // Scope collapsible section
  const [isScopeExpanded, setIsScopeExpanded] = useState(() => getSavedExpandedState('scope', true));

  // Milestones section
  const [isMilestonesExpanded, setIsMilestonesExpanded] = useState(() => getSavedExpandedState('milestones', true));
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [showMilestoneForm, setShowMilestoneForm] = useState(false);
  const [editingMilestoneId, setEditingMilestoneId] = useState<string | null>(null);
  const [milestoneFormData, setMilestoneFormData] = useState({ milestone_date: '', description: '' });

  // Search states
  const [projectTypeSearch, setProjectTypeSearch] = useState('');

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmationInput, setDeleteConfirmationInput] = useState('');
  const [deleteError, setDeleteError] = useState('');

  // Status dropdown
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);

  // Persona modal
  const [isPersonaModalOpen, setIsPersonaModalOpen] = useState(false);
  const [isPersonaEditMode, setIsPersonaEditMode] = useState(false);
  const [personaFormData, setPersonaFormData] = useState<CreatePersonaInput>({
    name: '',
    role: '',
    description: '',
    goals: '',
    pain_points: '',
    avatar_id: 'avatar-1',
    age_range: '',
    gender: '',
    location: ''
  });
  const [personaError, setPersonaError] = useState('');
  const [editingPersona, setEditingPersona] = useState<personaService.Persona | null>(null);
  const [isGeneratingPersona, setIsGeneratingPersona] = useState(false);

  // Executive Summary modal
  const [isExecutiveSummaryModalOpen, setIsExecutiveSummaryModalOpen] = useState(false);
  // BRD modal
  const [isBRDModalOpen, setIsBRDModalOpen] = useState(false);
  const [viewingPersona, setViewingPersona] = useState<personaService.Persona | null>(null);

  // Requirements Review modal
  const [isRequirementsReviewModalOpen, setIsRequirementsReviewModalOpen] = useState(false);
  const [requirementsReviewData, setRequirementsReviewData] = useState<RequirementsReviewData | null>(null);
  const [isGeneratingRequirementsReview, setIsGeneratingRequirementsReview] = useState(false);
  const [generationCount, setGenerationCount] = useState<number>(0);
  const [requirementsWithoutInsightsCount, setRequirementsWithoutInsightsCount] = useState<number>(0);

  // Project Approval modal
  const [isProjectApprovalModalOpen, setIsProjectApprovalModalOpen] = useState(false);
  const [approvalSummary, setApprovalSummary] = useState<ApprovalSummary | null>(null);

  // Persona delete confirmation
  const [showPersonaDeleteConfirm, setShowPersonaDeleteConfirm] = useState(false);
  const [personaToDelete, setPersonaToDelete] = useState<personaService.Persona | null>(null);

  // Stakeholder remove confirmation
  const [showStakeholderRemoveConfirm, setShowStakeholderRemoveConfirm] = useState(false);
  const [stakeholderToRemove, setStakeholderToRemove] = useState<{ memberId: string; name: string } | null>(null);

  const isStakeholder = currentUser?.role === 'stakeholder';

  const isOwner = project && currentUser && !isStakeholder && (
    members.some(m => m.user_id === currentUser.id && m.is_owner) ||
    currentUser.role === 'company_admin' ||
    currentUser.role === 'platform_admin'
  );

  const canEdit = isOwner && !isStakeholder;
  const canDelete = isOwner && !isStakeholder;
  const canManageMembers = isOwner && !isStakeholder;
  const canEditReports = currentUser && !isStakeholder && (
    currentUser.role === 'platform_admin' ||
    currentUser.role === 'company_admin' ||
    currentUser.role === 'project_manager' ||
    currentUser.role === 'project_manager_lite' ||
    (members.some(m => m.user_id === currentUser.id && m.is_owner))
  );

  const loadProject = async () => {
    if (!token || !id) return;
    setIsLoading(true);
    setError('');
    try {
      const data = await projectService.getProjectById(id, token);
      setProject(data.project);
      setMembers(data.members || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load project');
    } finally {
      setIsLoading(false);
    }
  };

  const loadCompanyUsers = async () => {
    if (!token) return;
    try {
      const data = await userService.listUsers({}, token);
      setCompanyUsers(data.users);
    } catch (err) {
      console.error('Failed to load company users:', err);
    }
  };

  const loadPersonas = async () => {
    if (!token || !id) return;
    try {
      const data = await personaService.getPersonas(id, token);
      setPersonas(data);
    } catch (err) {
      console.error('Failed to load personas:', err);
    }
  };

  const loadMilestones = async () => {
    if (!token || !id) return;
    try {
      const data = await milestoneService.listMilestones(id, token);
      setMilestones(data);
    } catch (err) {
      console.error('Failed to load milestones:', err);
    }
  };

  const loadProjectTechStack = async () => {
    if (!token || !id) return;
    try {
      const techStack = await projectService.getProjectTechStack(id, token);
      setProjectTechStack(techStack);
    } catch (err) {
      console.error('Failed to load project tech stack:', err);
    }
  };

  const loadRequirements = async () => {
    if (!token || !id) return;
    try {
      const data = await requirementService.getProjectRequirements(id, token);
      setRequirements(data || []);
    } catch (err) {
      console.error('Failed to load requirements:', err);
    }
  };

  const loadApprovalSummary = async () => {
    if (!token || !id) return;
    try {
      const data = await stakeholderApprovalService.getProjectApprovals(id, token);
      setApprovalSummary(data);
    } catch (err) {
      console.error('Failed to load approval summary:', err);
    }
  };

  const loadAvailableTechStack = async () => {
    if (!token) return;
    try {
      const items = await techStackService.listTechStackItems({ is_active: true }, token);
      setAvailableTechStack(items);
    } catch (err) {
      console.error('Failed to load available tech stack:', err);
    }
  };

  const loadUserPreferences = async () => {
    if (!token) return;
    try {
      const prefs = await userService.getUserPreferences(token);
      setPreferences(prefs);
      setPreferencesLoaded(true);
    } catch (err) {
      console.error('Failed to load user preferences:', err);
      // If loading fails, just mark as loaded so we use localStorage
      setPreferencesLoaded(true);
    }
  };

  // Debounced function to save preferences to API
  let savePreferencesTimeout: NodeJS.Timeout | null = null;
  const debouncedSavePreferences = (prefs: Record<string, boolean>) => {
    if (savePreferencesTimeout) {
      clearTimeout(savePreferencesTimeout);
    }
    savePreferencesTimeout = setTimeout(async () => {
      if (!token) return;
      try {
        await userService.updateUserPreferences(prefs, token);
      } catch (err) {
        console.error('Failed to save user preferences:', err);
      }
    }, 1000); // Wait 1 second after last change before saving
  };

  // Load user preferences on mount
  useEffect(() => {
    loadUserPreferences();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    loadProject();
    loadCompanyUsers();
    loadProjectTechStack();
    loadRequirements();
    loadPersonas();
    loadMilestones();
    loadApprovalSummary();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token]);

  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError('');
        setSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  useEffect(() => {
    const loadCurrentProjectType = async () => {
      if (!token || !project || !project.project_type_id) {
        setCurrentProjectType(null);
        return;
      }
      try {
        const projectType = await projectTypeService.getProjectTypeById(project.project_type_id, token);
        setCurrentProjectType(projectType);
      } catch (err) {
        console.error('Failed to load project type:', err);
        setCurrentProjectType(null);
      }
    };
    loadCurrentProjectType();
  }, [project, token]);

  const loadProjectTypes = async () => {
    if (!token) return;
    try {
      const types = await projectTypeService.listProjectTypes({ is_active: true }, token);
      setProjectTypes(types);
    } catch (err) {
      console.error('Failed to load project types:', err);
    }
  };

  const handleEditClick = async () => {
    if (!project) return;
    setEditFormData({
      name: project.name,
      project_code: project.project_code,
      description: project.description || '',
      project_type_id: project.project_type_id || '',
      status: project.status,
      start_date: project.start_date || '',
      target_completion_date: project.target_completion_date || '',
      tags: project.tags || ''
    });
    setEditError('');
    setProjectTypeSearch('');
    await loadProjectTypes();
    setIsEditing(true);
  };

  const handleInlineEditClick = async () => {
    if (!project || !canEdit) return;
    setInlineEditData({
      description: project.description || '',
      project_type_id: project.project_type_id || '',
      start_date: formatDateForInput(project.start_date),
      target_completion_date: formatDateForInput(project.target_completion_date),
      tags: project.tags || ''
    });
    // Load project types for the dropdown
    await loadProjectTypes();
    setIsInlineEditing(true);
  };

  const handleInlineSave = async () => {
    if (!token || !id) return;
    try {
      const updated = await projectService.updateProject(id, inlineEditData, token);
      setProject(updated);
      setSuccess('Project updated successfully');
      setIsInlineEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update project');
    }
  };

  const handleInlineCancel = () => {
    setIsInlineEditing(false);
    setInlineEditData({});
  };

  const handleStatusChange = async (newStatus: 'draft' | 'ready' | 'in_progress' | 'done') => {
    if (!token || !id || !canEdit) return;
    try {
      const updated = await projectService.updateProject(id, { status: newStatus }, token);
      setProject(updated);
      setSuccess('Project status updated');
      setShowStatusDropdown(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
      setShowStatusDropdown(false);
    }
  };

  // Scope section handlers (separate from project info)
  const handleScopeEditClick = () => {
    if (!project || !canEdit) return;
    setScopeEditData({
      in_scope: project.in_scope || '',
      out_scope: project.out_scope || ''
    });
    setIsEditingScope(true);
  };

  const handleScopeSave = async () => {
    if (!token || !id) return;
    try {
      const updated = await projectService.updateProject(id, scopeEditData, token);
      setProject(updated);
      setSuccess('Scope updated successfully');
      setIsEditingScope(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update scope');
    }
  };

  const handleScopeCancel = () => {
    setIsEditingScope(false);
    setScopeEditData({ in_scope: '', out_scope: '' });
  };

  const handleEditProject = async () => {
    if (!token || !id) return;
    setEditError('');
    try {
      const updated = await projectService.updateProject(id, editFormData, token);
      setProject(updated);
      setSuccess('Project updated successfully');
      setIsEditing(false);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Failed to update project');
    }
  };

  const handleDeleteProject = async () => {
    if (!token || !id) return;
    setDeleteError('');
    try {
      await projectService.deleteProject(id, token);
      setSuccess('Project deleted successfully');
      setTimeout(() => navigate('/projects'), 1500);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete project');
    }
  };

  const handleAddMemberClick = () => {
    setAddMemberData({ user_id: '', role: 'contributor' });
    setAddMemberError('');
    setIsAddingMember(true);
  };

  const handleAddMember = async () => {
    if (!token || !id || !addMemberData.user_id) {
      setAddMemberError('Please select a user');
      return;
    }
    setAddMemberError('');
    try {
      await projectService.addProjectMember(id, addMemberData, token);
      setSuccess('Member added successfully');
      setIsAddingMember(false);
      loadProject();
    } catch (err) {
      setAddMemberError(err instanceof Error ? err.message : 'Failed to add member');
    }
  };

  const handleAddStakeholderClick = () => {
    setAddStakeholderData({ stakeholder_name: '', stakeholder_responsibility: '', role: 'stakeholder' });
    setAddStakeholderError('');
    setIsAddingStakeholder(true);
  };

  const handleAddStakeholder = async () => {
    if (!token || !id || !addStakeholderData.stakeholder_name) {
      setAddStakeholderError('Please enter stakeholder name');
      return;
    }
    setAddStakeholderError('');
    try {
      await projectService.addProjectMember(id, addStakeholderData, token);
      setSuccess('Stakeholder added successfully');
      setIsAddingStakeholder(false);
      loadProject();
    } catch (err) {
      setAddStakeholderError(err instanceof Error ? err.message : 'Failed to add stakeholder');
    }
  };

  const handleEditStakeholderClick = (member: ProjectMember) => {
    setEditStakeholderMemberId(member.id);
    setEditStakeholderData({
      stakeholder_name: member.stakeholder_name || '',
      stakeholder_responsibility: member.stakeholder_responsibility || ''
    });
    setEditStakeholderError('');
    setIsEditingStakeholder(true);
  };

  const handleEditStakeholder = async () => {
    if (!token || !id || !editStakeholderMemberId || !editStakeholderData.stakeholder_name) {
      setEditStakeholderError('Please enter stakeholder name');
      return;
    }
    setEditStakeholderError('');
    try {
      await projectService.updateStakeholder(id, editStakeholderMemberId, editStakeholderData, token);
      setSuccess('Stakeholder updated successfully');
      setIsEditingStakeholder(false);
      loadProject();
    } catch (err) {
      setEditStakeholderError(err instanceof Error ? err.message : 'Failed to update stakeholder');
    }
  };

  const handleRemoveStakeholder = (memberId: string, stakeholderName: string) => {
    setStakeholderToRemove({ memberId, name: stakeholderName });
    setShowStakeholderRemoveConfirm(true);
  };

  const confirmRemoveStakeholder = async () => {
    if (!token || !id || !stakeholderToRemove) return;

    try {
      await projectService.removeStakeholder(id, stakeholderToRemove.memberId, token);
      setSuccess(`Stakeholder "${stakeholderToRemove.name}" removed successfully`);
      setShowStakeholderRemoveConfirm(false);
      setStakeholderToRemove(null);
      loadProject();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove stakeholder');
      setShowStakeholderRemoveConfirm(false);
      setStakeholderToRemove(null);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!token || !id) return;
    if (!window.confirm('Are you sure you want to remove this member?')) return;

    try {
      await projectService.removeProjectMember(id, userId, token);
      setSuccess('Member removed successfully');
      loadProject();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove member');
    }
  };

  const handleAddTechStackClick = async () => {
    setSelectedTechStackId('');
    setAddTechStackError('');
    setTechStackSearch('');
    await loadAvailableTechStack();
    setIsAddingTechStack(true);
  };

  const handleAddTechStack = async () => {
    if (!token || !id || !selectedTechStackId) {
      setAddTechStackError('Please select a technology');
      return;
    }
    setAddTechStackError('');
    try {
      await projectService.addProjectTechStack(id, { tech_stack_item_id: selectedTechStackId }, token);
      setSuccess('Technology added successfully');
      setIsAddingTechStack(false);
      loadProjectTechStack();
    } catch (err) {
      setAddTechStackError(err instanceof Error ? err.message : 'Failed to add technology');
    }
  };

  const handleRemoveTechStack = async (techStackItemId: string) => {
    if (!token || !id) return;
    if (!window.confirm('Are you sure you want to remove this technology from the project?')) return;

    try {
      await projectService.removeProjectTechStack(id, techStackItemId, token);
      setSuccess('Technology removed successfully');
      loadProjectTechStack();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove technology');
    }
  };

  const handleAddPersonaClick = () => {
    setEditingPersona(null);
    setPersonaFormData({
      name: '',
      role: '',
      description: '',
      goals: '',
      pain_points: '',
      avatar_id: 'avatar-1',
      age_range: '',
      gender: '',
      location: ''
    });
    setPersonaError('');
    setIsPersonaModalOpen(true);
  };

  const handleCreatePersona = async () => {
    if (!token || !id) return;
    if (!personaFormData.name.trim()) {
      setPersonaError('Persona name is required');
      return;
    }
    setPersonaError('');
    try {
      await personaService.createPersona(id, personaFormData, token);
      setSuccess('Persona created successfully');
      setIsPersonaModalOpen(false);
      loadPersonas();
    } catch (err) {
      setPersonaError(err instanceof Error ? err.message : 'Failed to create persona');
    }
  };

  const handleGeneratePersonaWithAI = async () => {
    if (!token || !id) return;

    setIsGeneratingPersona(true);
    setPersonaError('');

    try {
      const response = await personaService.generatePersonaWithAI(id, token);

      // Populate form fields with AI-generated data
      setPersonaFormData({
        ...personaFormData,
        name: response.persona.name,
        role: response.persona.role,
        description: response.persona.description,
        goals: response.persona.goals,
        pain_points: response.persona.pain_points,
        age_range: response.persona.age_range,
        gender: response.persona.gender,
        location: response.persona.location,
        // Keep the currently selected avatar_id - user can change it
      });

      setSuccess(`Persona generated successfully using ${response.metadata.model}`);
    } catch (err: any) {
      setPersonaError(err.response?.data?.error || err.message || 'Failed to generate persona with AI');
    } finally {
      setIsGeneratingPersona(false);
    }
  };

  const handleShowRequirementsInsights = async () => {
    if (!token || !id) return;

    setError('');

    try {
      // Try to load existing insights first
      const existingInsights = await projectService.getRequirementsInsights(id, token);

      if (existingInsights) {
        // Show existing insights
        setRequirementsReviewData(existingInsights);
        setGenerationCount(existingInsights.generationCount || 0);
        setRequirementsWithoutInsightsCount(existingInsights.requirementsWithoutInsightsCount || 0);
        setIsRequirementsReviewModalOpen(true);
      } else {
        // No existing insights, check if we can generate (under limit)
        const currentCount = project?.requirements_insights_generation_count || 0;
        if (currentCount >= 10) {
          setError('Generation limit reached (10/10). You cannot regenerate all insights.');
          return;
        }

        // Generate new ones
        setIsGeneratingRequirementsReview(true);
        setIsRequirementsReviewModalOpen(true);
        const reviewData = await projectService.generateRequirementsReview(id, token, false);
        setRequirementsReviewData(reviewData);
        setGenerationCount(reviewData.generationCount || 0);
        setRequirementsWithoutInsightsCount(reviewData.requirementsWithoutInsightsCount || 0);
        setSuccess('Requirements insights generated successfully');
        // Reload requirements and project to show updated insights and count
        loadRequirements();
        loadProject();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load requirements insights');
      setIsRequirementsReviewModalOpen(false);
    } finally {
      setIsGeneratingRequirementsReview(false);
    }
  };

  const handleGenerateRequirementsReview = async () => {
    if (!token || !id) return;

    // Check limit before attempting
    const currentCount = generationCount || 0;
    if (currentCount >= 10) {
      setError('Generation limit reached (10/10). Cannot regenerate all insights.');
      return;
    }

    setIsGeneratingRequirementsReview(true);
    setError('');
    setSuccess('');

    try {
      const reviewData = await projectService.generateRequirementsReview(id, token, false);
      setRequirementsReviewData(reviewData);
      setGenerationCount(reviewData.generationCount || 0);
      setRequirementsWithoutInsightsCount(reviewData.requirementsWithoutInsightsCount || 0);
      setSuccess('Requirements insights regenerated successfully');
      // Reload requirements and project to show updated insights and count
      loadRequirements();
      loadProject();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to regenerate requirements insights');
    } finally {
      setIsGeneratingRequirementsReview(false);
    }
  };

  const handleGenerateNewRequirementsInsights = async () => {
    if (!token || !id) return;

    setError('');
    setSuccess('');
    setIsGeneratingRequirementsReview(true);

    try {
      const reviewData = await projectService.generateRequirementsReview(id, token, true);
      setRequirementsReviewData(reviewData);
      setGenerationCount(reviewData.generationCount || 0);
      setRequirementsWithoutInsightsCount(reviewData.requirementsWithoutInsightsCount || 0);
      setSuccess('Insights generated for new requirements successfully');
      loadRequirements();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to generate insights for new requirements');
    } finally {
      setIsGeneratingRequirementsReview(false);
    }
  };

  const handleDeletePersona = (persona: personaService.Persona) => {
    setPersonaToDelete(persona);
    setShowPersonaDeleteConfirm(true);
  };

  const confirmDeletePersona = async () => {
    if (!token || !id || !personaToDelete) return;

    try {
      await personaService.deletePersona(id, personaToDelete.id, token);
      setSuccess('Persona deleted successfully');
      setShowPersonaDeleteConfirm(false);
      setPersonaToDelete(null);
      loadPersonas();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete persona');
      setShowPersonaDeleteConfirm(false);
      setPersonaToDelete(null);
    }
  };

  const handleViewPersonaClick = (persona: personaService.Persona) => {
    setViewingPersona(persona);
    setEditingPersona(null);
    setPersonaFormData({
      name: persona.name,
      role: persona.role || '',
      description: persona.description || '',
      goals: persona.goals || '',
      pain_points: persona.pain_points || '',
      avatar_id: persona.avatar_id,
      age_range: persona.age_range || '',
      gender: persona.gender || '',
      location: persona.location || ''
    });
    setPersonaError('');
    setIsPersonaEditMode(false);
    setIsPersonaModalOpen(true);
  };

  const handlePersonaEditClick = () => {
    if (viewingPersona) {
      setEditingPersona(viewingPersona);
      setIsPersonaEditMode(true);
    }
  };

  const handlePersonaEditCancel = () => {
    if (viewingPersona) {
      setPersonaFormData({
        name: viewingPersona.name,
        role: viewingPersona.role || '',
        description: viewingPersona.description || '',
        goals: viewingPersona.goals || '',
        pain_points: viewingPersona.pain_points || '',
        avatar_id: viewingPersona.avatar_id,
        age_range: viewingPersona.age_range || '',
        gender: viewingPersona.gender || '',
        location: viewingPersona.location || ''
      });
    }
    setIsPersonaEditMode(false);
    setEditingPersona(null);
    setPersonaError('');
  };

  const handleUpdatePersona = async () => {
    if (!token || !id || !editingPersona) return;
    if (!personaFormData.name.trim()) {
      setPersonaError('Persona name is required');
      return;
    }
    setPersonaError('');
    try {
      await personaService.updatePersona(id, editingPersona.id, personaFormData, token);
      setSuccess('Persona updated successfully');
      setIsPersonaEditMode(false);
      setEditingPersona(null);
      // Reload personas to get updated data
      await loadPersonas();
      // Update the viewing persona with new data
      const updatedPersonas = await personaService.getPersonas(id, token);
      const updatedPersona = updatedPersonas.find(p => p.id === editingPersona.id);
      if (updatedPersona) {
        setViewingPersona(updatedPersona);
        setPersonaFormData({
          name: updatedPersona.name,
          role: updatedPersona.role || '',
          description: updatedPersona.description || '',
          goals: updatedPersona.goals || '',
          pain_points: updatedPersona.pain_points || '',
          avatar_id: updatedPersona.avatar_id,
          age_range: updatedPersona.age_range || '',
          gender: updatedPersona.gender || '',
          location: updatedPersona.location || ''
        });
      }
    } catch (err) {
      setPersonaError(err instanceof Error ? err.message : 'Failed to update persona');
    }
  };

  const handleToggleTechStack = async (techName: string) => {
    if (!token || !id || !canEdit) return;

    // Check if this tech is already in the project (case-insensitive)
    const existingTech = projectTechStack.find(
      tech => tech.tech_name.toLowerCase() === techName.toLowerCase()
    );

    if (existingTech) {
      // Remove it
      try {
        await projectService.removeProjectTechStack(id, existingTech.tech_stack_item_id, token);
        setSuccess(`${techName} removed successfully`);
        await loadProjectTechStack();
      } catch (err) {
        setError(err instanceof Error ? err.message : `Failed to remove ${techName}`);
      }
    } else {
      // Add it - always load fresh tech stack to ensure we have the latest
      try {
        const freshTechStack = await techStackService.listTechStackItems({ is_active: true }, token);

        // Try to find exact match first (case-insensitive)
        let techItem = freshTechStack.find(
          tech => tech.name.toLowerCase() === techName.toLowerCase()
        );

        // If no exact match, try partial match
        if (!techItem) {
          techItem = freshTechStack.find(tech =>
            tech.name.toLowerCase().includes(techName.toLowerCase()) ||
            techName.toLowerCase().includes(tech.name.toLowerCase())
          );
        }

        if (techItem) {
          try {
            await projectService.addProjectTechStack(id, { tech_stack_item_id: techItem.id }, token);
            setSuccess(`${techItem.name} added successfully`);
            await loadProjectTechStack();
          } catch (err) {
            setError(err instanceof Error ? err.message : `Failed to add ${techName}`);
          }
        } else {
          // Log available techs for debugging
          console.log('Available technologies:', freshTechStack.map(t => t.name));
          console.log('Looking for:', techName);
          setError(`Technology "${techName}" is not available in the system. Please use "Add other technology" to add it.`);
        }
      } catch (err) {
        console.error('Error loading tech stack:', err);
        setError('Failed to load available technologies');
      }
    }
  };

  const handleTechStackExpandToggle = async () => {
    if (!isTechStackExpanded && availableTechStack.length === 0) {
      await loadAvailableTechStack();
    }
    const newState = !isTechStackExpanded;
    setIsTechStackExpanded(newState);
    saveExpandedState('techStack', newState);
  };

  // Parse a date string as local time (avoids UTC midnight shift that causes off-by-one day)
  const parseLocalDate = (dateString: string): Date => {
    const parts = dateString.split('T')[0].split('-');
    return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Not set';
    return parseLocalDate(dateString).toLocaleDateString();
  };

  // Helper function to convert ISO date to YYYY-MM-DD format for date inputs
  const formatDateForInput = (dateString: string | null | undefined) => {
    if (!dateString) return '';
    const date = parseLocalDate(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-400',
      ready: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400',
      in_progress: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400',
      done: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400'
    };
    return colors[status] || 'bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-400';
  };

  const getStatusDisplay = (status: string) => {
    const names: Record<string, string> = {
      draft: 'Draft',
      ready: 'Ready',
      in_progress: 'In Progress',
      done: 'Done'
    };
    return names[status] || status;
  };

  // Calculate requirements counts
  const getRequirementCounts = () => {
    const totalCount = requirements.filter(r => r.is_active).length;

    const byType = {
      functional: requirements.filter(r => r.is_active && r.requirement_type === 'functional').length,
      'non-functional': requirements.filter(r => r.is_active && r.requirement_type === 'non-functional').length,
      technical: requirements.filter(r => r.is_active && r.requirement_type === 'technical').length,
      business: requirements.filter(r => r.is_active && r.requirement_type === 'business').length,
      'user-story': requirements.filter(r => r.is_active && r.requirement_type === 'user-story').length,
    };

    // Count by approval status
    const byApprovalStatus = {
      approved: requirements.filter(r => r.is_active && r.approval_status === 'approved').length,
      pending: requirements.filter(r => r.is_active && r.approval_status === 'pending').length,
      rejected: requirements.filter(r => r.is_active && r.approval_status === 'rejected').length,
    };

    return { totalCount, byType, byApprovalStatus };
  };

  const requirementCounts = getRequirementCounts();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background-secondary flex items-center justify-center">
        <div className="text-text-tertiary">Loading project...</div>
      </div>
    );
  }

  if (error && !project) {
    return (
      <Layout>
        <div className="py-8 px-4 sm:px-6 lg:px-8">
          <div className="bg-surface-primary rounded-lg shadow p-8 text-center">
            <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
            <button
              onClick={() => navigate('/projects')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-full text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Back to Projects
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  if (!project) return null;

  // Filter out users who are already members
  const availableUsers = companyUsers.filter(
    user => !members.some(m => m.user_id === user.id)
  );

  return (
    <Layout>
      <div className="py-8 px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-blue-400 rounded-lg shadow-lg mb-6 p-6">
          <div className="flex justify-between items-center">
            <div>
              <button
                onClick={() => navigate('/projects')}
                className="text-sm text-blue-100 hover:text-white mb-2 inline-flex items-center transition-colors"
              >
                ← Back to Projects
              </button>
              <h1 className="text-3xl font-bold text-white">
                {project.name}
              </h1>
            </div>
            {canDelete && (
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => { setDeleteError(''); setShowDeleteConfirm(true); }}
                  className="inline-flex items-center px-4 py-2 border border-border-primary text-sm font-medium rounded-full text-red-600 dark:text-red-400 bg-surface-primary hover:bg-background-secondary"
                >
                  Delete Project
                </button>
              </div>
            )}
          </div>
        </div>
        {error && (
          <div className="mb-4 p-4 rounded-md bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800">
            <p className="text-sm text-red-800 dark:text-red-400">{error}</p>
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 rounded-md bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800">
            <p className="text-sm text-green-800 dark:text-green-400">{success}</p>
          </div>
        )}

        {/* Project Timeline */}
        {project.start_date && project.target_completion_date && (() => {
          const s = parseLocalDate(project.start_date!);
          const e = parseLocalDate(project.target_completion_date!);
          if (isNaN(s.getTime()) || isNaN(e.getTime()) || e <= s) return null;
          const days = Math.round((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24));
          const fmtDate = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

          // Calculate progress based on today's date
          const now = new Date();
          const totalMs = e.getTime() - s.getTime();
          const elapsedMs = now.getTime() - s.getTime();
          const progress = Math.max(0, Math.min(100, (elapsedMs / totalMs) * 100));
          const daysRemaining = Math.max(0, Math.ceil((e.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
          const isOverdue = now > e;

          return (
            <div className="bg-surface-primary rounded-lg shadow p-5 mb-6">
              <div className="flex items-center gap-5">
                {/* Timeline bar section */}
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-semibold tracking-widest text-text-tertiary uppercase mb-2">Project Timeline</p>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-semibold text-text-primary">{fmtDate(s)}</span>
                    <span className="text-xs font-semibold text-text-primary">{fmtDate(e)}</span>
                  </div>
                  <div className="relative h-2.5 rounded-full bg-gray-200 dark:bg-gray-700">
                    {/* Progress fill */}
                    <div
                      className={`absolute inset-y-0 left-0 rounded-full ${isOverdue ? 'bg-gradient-to-r from-red-500 to-red-400' : 'bg-gradient-to-r from-indigo-500 to-blue-500'}`}
                      style={{ width: `${progress}%` }}
                    />
                    {/* Start dot */}
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-3.5 h-3.5 rounded-full bg-white dark:bg-gray-800 border-2 border-indigo-500 shadow-sm" />
                    {/* End dot */}
                    <div className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-1 w-3.5 h-3.5 rounded-full bg-white dark:bg-gray-800 border-2 ${isOverdue ? 'border-red-500' : 'border-blue-500'} shadow-sm`} />
                    {/* Today marker */}
                    {progress > 0 && progress < 100 && (
                      <div
                        className="absolute top-1/2 -translate-y-1/2 w-1 h-5 bg-slate-600 dark:bg-slate-300 rounded-full shadow"
                        style={{ left: `${progress}%` }}
                        title={`Today — ${daysRemaining} days remaining`}
                      />
                    )}
                    {/* Requirement due-date markers */}
                    {requirements
                      .filter((req) => req.due_date && req.is_active)
                      .map((req) => {
                        const dueMs = parseLocalDate(req.due_date!).getTime();
                        const pct = Math.max(0, Math.min(100, ((dueMs - s.getTime()) / (e.getTime() - s.getTime())) * 100));
                        return (
                          <div
                            key={req.id}
                            className="group absolute top-1/2 -translate-y-1/2 -translate-x-[5px] z-10"
                            style={{ left: `${pct}%` }}
                          >
                            <div className="w-2.5 h-2.5 rounded-full bg-white dark:bg-gray-800 border-2 border-gray-800 dark:border-gray-200 cursor-pointer shadow-sm" />
                            <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50">
                              <div className="bg-white dark:bg-gray-800 shadow-lg rounded-md px-2.5 py-1.5 text-xs whitespace-nowrap border border-gray-200 dark:border-gray-700">
                                <div>
                                  <span className="font-semibold text-text-primary">{req.requirement_code || 'REQ'}</span>
                                  <span className="text-text-secondary"> — {req.title.length > 40 ? req.title.slice(0, 40) + '…' : req.title}</span>
                                </div>
                                <div className="mt-1 text-[11px] text-text-tertiary space-y-0.5">
                                  <div>Start: {req.expected_start_date ? formatDate(req.expected_start_date) : 'Not set'}</div>
                                  <div>Due: {formatDate(req.due_date)}</div>
                                  <div>Duration: {req.expected_start_date && req.due_date
                                    ? `${Math.round((parseLocalDate(req.due_date).getTime() - parseLocalDate(req.expected_start_date).getTime()) / (1000 * 60 * 60 * 24))} days`
                                    : 'N/A'}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    {/* Milestone markers (diamonds) */}
                    {milestones
                      .filter((m) => m.milestone_date)
                      .map((m) => {
                        const mDate = parseLocalDate(m.milestone_date.toString().slice(0, 10));
                        const pct = Math.max(0, Math.min(100, ((mDate.getTime() - s.getTime()) / (e.getTime() - s.getTime())) * 100));
                        return (
                          <div
                            key={`ms-${m.id}`}
                            className="group absolute top-1/2 -translate-y-1/2 -translate-x-[7px] z-20"
                            style={{ left: `${pct}%` }}
                          >
                            <div className="w-3.5 h-3.5 rotate-45 bg-indigo-500 border-2 border-white dark:border-gray-800 cursor-pointer shadow-sm" />
                            <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50">
                              <div className="bg-white dark:bg-gray-800 shadow-lg rounded-md px-2.5 py-1.5 text-xs whitespace-nowrap border border-gray-200 dark:border-gray-700">
                                <div className="font-semibold text-indigo-600 dark:text-indigo-400">Milestone</div>
                                <div className="text-text-primary">{m.description}</div>
                                <div className="mt-1 text-[11px] text-text-tertiary">
                                  {mDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                  {/* Progress label */}
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="text-[10px] text-text-tertiary">
                      {isOverdue ? 'Overdue' : `${Math.round(progress)}% elapsed`}
                    </span>
                    <span className={`text-[10px] font-medium ${isOverdue ? 'text-red-500' : 'text-text-tertiary'}`}>
                      {isOverdue ? `${Math.abs(daysRemaining)} days overdue` : `${daysRemaining} days remaining`}
                    </span>
                  </div>
                </div>
                {/* Hero badge */}
                <div className={`shrink-0 flex flex-col items-center justify-center w-20 h-20 rounded-xl border ${isOverdue ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' : 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800'}`}>
                  <span className={`text-2xl font-bold leading-none ${isOverdue ? 'text-red-700 dark:text-red-400' : 'text-indigo-700 dark:text-indigo-400'}`}>{days}</span>
                  <span className={`text-[10px] font-semibold mt-1 tracking-wide ${isOverdue ? 'text-red-500 dark:text-red-400' : 'text-indigo-500 dark:text-indigo-400'}`}>Est. Days</span>
                </div>
              </div>
            </div>
          );
        })()}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Project Info Card */}
            <div className="bg-surface-primary rounded-lg shadow p-6">
              {/* Collapsible Header */}
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => {
                  const newState = !isProjectInfoExpanded;
                  setIsProjectInfoExpanded(newState);
                  saveExpandedState('projectInfo', newState);
                }}
              >
                <div className="flex items-center gap-2">
                  <svg
                    className={`h-5 w-5 text-text-tertiary transition-transform duration-200 ${isProjectInfoExpanded ? 'rotate-90' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <h2 className="text-lg font-semibold text-text-primary">Project Information</h2>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-text-secondary">
                    {project.project_code}
                  </span>
                  <div className="relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (canEdit) setShowStatusDropdown(!showStatusDropdown);
                      }}
                      className={'px-3 py-1 rounded-full text-sm font-medium ' + getStatusColor(project.status) + (canEdit ? ' cursor-pointer hover:ring-2 hover:ring-indigo-300 dark:hover:ring-indigo-600' : '')}
                      disabled={!canEdit}
                      title={canEdit ? 'Click to change status' : 'You do not have permission to change status'}
                    >
                      {getStatusDisplay(project.status)}
                    </button>
                    {showStatusDropdown && canEdit && (
                      <>
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setShowStatusDropdown(false)}
                        />
                        <div className="absolute right-0 mt-1 w-40 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-20">
                          <div className="py-1">
                            {(['draft', 'ready', 'in_progress', 'done'] as const).map((status) => (
                              <button
                                key={status}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStatusChange(status);
                                }}
                                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                                  project.status === status
                                    ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 font-medium'
                                    : 'text-gray-700 dark:text-gray-300'
                                }`}
                              >
                                {getStatusDisplay(status)}
                              </button>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Collapsible Content */}
              {isProjectInfoExpanded && (
                <div className="mt-4 pt-4 border-t border-border-primary">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Column - Project Details */}
                    <div className="lg:col-span-2 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-text-tertiary">Description</label>
                        {isInlineEditing ? (
                          <div className="mt-1">
                            <RichTextEditor
                              value={inlineEditData.description || ''}
                              onChange={(html) => setInlineEditData({ ...inlineEditData, description: html })}
                              placeholder="Enter project description..."
                              minHeight="80px"
                            />
                          </div>
                        ) : (
                          <div className="mt-1">
                            {project.description ? (
                              <RichTextDisplay content={project.description} />
                            ) : (
                              <p className="text-sm text-text-tertiary">No description provided</p>
                            )}
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-text-tertiary">Project Type</label>
                        {isInlineEditing ? (
                          <select
                            value={inlineEditData.project_type_id || ''}
                            onChange={(e) => setInlineEditData({ ...inlineEditData, project_type_id: e.target.value })}
                            className="mt-1 w-full px-3 py-2 border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          >
                            <option value="">-- Select a project type --</option>
                            {projectTypes.map((type) => (
                              <option key={type.id} value={type.id}>
                                {type.name} ({type.category})
                              </option>
                            ))}
                          </select>
                        ) : (
                          <p className="mt-1 text-sm text-text-primary">
                            {currentProjectType ? (
                              <>
                                {currentProjectType.name}
                                <span className="ml-2 text-text-tertiary">({currentProjectType.category})</span>
                              </>
                            ) : (
                              <span className="text-text-tertiary">Not selected</span>
                            )}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-text-tertiary">Tags</label>
                        {isInlineEditing ? (
                          <input
                            type="text"
                            value={inlineEditData.tags || ''}
                            onChange={(e) => setInlineEditData({ ...inlineEditData, tags: e.target.value })}
                            className="mt-1 w-full px-3 py-2 border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="Comma-separated tags..."
                          />
                        ) : (
                          <p className="mt-1 text-sm text-text-primary">
                            {project.tags || <span className="text-text-tertiary">No tags</span>}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Right Column - Important Dates */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-text-secondary uppercase tracking-wide">Important Dates</h3>
                        {canEdit && !isInlineEditing && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleInlineEditClick();
                            }}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-surface-primary border border-border-primary rounded-full text-sm font-medium text-text-secondary hover:bg-background-secondary hover:border-gray-400 transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                            Edit
                          </button>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-text-tertiary">Start Date</label>
                        {isInlineEditing ? (
                          <input
                            type="date"
                            value={inlineEditData.start_date || ''}
                            onChange={(e) => setInlineEditData({ ...inlineEditData, start_date: e.target.value })}
                            className="mt-1 w-full px-2 py-1 text-sm border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        ) : (
                          <p className="mt-1 text-sm text-text-primary">{formatDate(project.start_date)}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-text-tertiary">Target Completion</label>
                        {isInlineEditing ? (
                          <input
                            type="date"
                            value={inlineEditData.target_completion_date || ''}
                            onChange={(e) => setInlineEditData({ ...inlineEditData, target_completion_date: e.target.value })}
                            className="mt-1 w-full px-2 py-1 text-sm border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        ) : (
                          <p className="mt-1 text-sm text-text-primary">{formatDate(project.target_completion_date)}</p>
                        )}
                      </div>

                      {project.actual_completion_date && (
                        <div>
                          <label className="block text-xs font-medium text-text-tertiary">Actual Completion</label>
                          <p className="mt-1 text-sm text-text-primary">{formatDate(project.actual_completion_date)}</p>
                        </div>
                      )}

                      <div className="pt-3 border-t border-border-primary">
                        <label className="block text-xs font-medium text-text-tertiary">Created</label>
                        <p className="mt-1 text-sm text-text-primary">{formatDate(project.created_at)}</p>
                      </div>

                      {project.updated_at && (
                        <div>
                          <label className="block text-xs font-medium text-text-tertiary">Last Updated</label>
                          <p className="mt-1 text-sm text-text-primary">{formatDate(project.updated_at)}</p>
                        </div>
                      )}

                      {/* Save/Cancel buttons when editing */}
                      {isInlineEditing && (
                        <div className="pt-4 border-t border-border-primary flex gap-2">
                          <button
                            onClick={handleInlineSave}
                            className="flex-1 px-3 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 rounded-md hover:from-indigo-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          >
                            Save
                          </button>
                          <button
                            onClick={handleInlineCancel}
                            className="flex-1 px-3 py-2 text-sm font-medium text-text-secondary bg-surface-primary border border-border-primary rounded-md hover:bg-background-secondary focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Scope Card */}
            {!isStakeholder && (<><div className="bg-surface-primary rounded-lg shadow p-6">
              {/* Collapsible Header */}
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => {
                  const newState = !isScopeExpanded;
                  setIsScopeExpanded(newState);
                  saveExpandedState('scope', newState);
                }}
              >
                <div className="flex items-center gap-2">
                  <svg
                    className={`h-5 w-5 text-text-tertiary transition-transform duration-200 ${isScopeExpanded ? 'rotate-90' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <h2 className="text-lg font-semibold text-text-primary">Scope</h2>
                </div>
                <div className="flex items-center gap-3">
                  {canEdit && !isEditingScope && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleScopeEditClick();
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-surface-primary border border-border-primary rounded-full text-sm font-medium text-text-secondary hover:bg-background-secondary hover:border-gray-400 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                      Edit
                    </button>
                  )}
                  <svg
                    className={`h-5 w-5 text-text-tertiary transition-transform duration-200 ${isScopeExpanded ? 'rotate-90' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>

              {/* Collapsible Content */}
              {isScopeExpanded && (
                <div className="mt-4 pt-4 border-t border-border-primary">
                  <div className="space-y-6">
                    {/* In Scope */}
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">In Scope</label>
                      {isEditingScope ? (
                        <textarea
                          value={scopeEditData.in_scope || ''}
                          onChange={(e) => setScopeEditData({ ...scopeEditData, in_scope: e.target.value })}
                          rows={4}
                          className="w-full px-3 py-2 border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="What is included in this project's scope..."
                        />
                      ) : (
                        <div className="text-sm text-text-primary whitespace-pre-wrap bg-background-secondary rounded-md p-3 border border-border-primary">
                          {project.in_scope || <span className="text-text-tertiary">Not specified</span>}
                        </div>
                      )}
                    </div>

                    {/* Out of Scope */}
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">Out of Scope</label>
                      {isEditingScope ? (
                        <textarea
                          value={scopeEditData.out_scope || ''}
                          onChange={(e) => setScopeEditData({ ...scopeEditData, out_scope: e.target.value })}
                          rows={4}
                          className="w-full px-3 py-2 border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="What is explicitly excluded from this project's scope..."
                        />
                      ) : (
                        <div className="text-sm text-text-primary whitespace-pre-wrap bg-background-secondary rounded-md p-3 border border-border-primary">
                          {project.out_scope || <span className="text-text-tertiary">Not specified</span>}
                        </div>
                      )}
                    </div>

                    {/* Save/Cancel buttons when editing */}
                    {isEditingScope && (
                      <div className="pt-4 border-t border-border-primary flex gap-2">
                        <button
                          onClick={handleScopeSave}
                          className="flex-1 px-3 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 rounded-md hover:from-indigo-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          Save
                        </button>
                        <button
                          onClick={handleScopeCancel}
                          className="flex-1 px-3 py-2 text-sm font-medium text-text-secondary bg-surface-primary border border-border-primary rounded-md hover:bg-background-secondary focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Milestones Card */}
            {!isStakeholder && (
            <div className="bg-surface-primary rounded-lg shadow p-6">
              {/* Collapsible Header */}
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => {
                  const newState = !isMilestonesExpanded;
                  setIsMilestonesExpanded(newState);
                  saveExpandedState('milestones', newState);
                }}
              >
                <div className="flex items-center gap-2">
                  <svg
                    className={`h-5 w-5 text-text-tertiary transition-transform duration-200 ${isMilestonesExpanded ? 'rotate-90' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <svg className="h-5 w-5 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
                  </svg>
                  <h2 className="text-lg font-semibold text-text-primary">Milestones</h2>
                  {milestones.length > 0 && (
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-r from-indigo-100 to-blue-100 border border-indigo-200">
                      <span className="text-sm font-semibold text-indigo-700 dark:text-indigo-400">{milestones.length}</span>
                    </div>
                  )}
                </div>
                {canEdit && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setMilestoneFormData({ milestone_date: '', description: '' });
                      setEditingMilestoneId(null);
                      setShowMilestoneForm(true);
                      if (!isMilestonesExpanded) {
                        setIsMilestonesExpanded(true);
                        saveExpandedState('milestones', true);
                      }
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-surface-primary border border-border-primary rounded-full text-sm font-medium text-text-secondary hover:bg-background-secondary hover:border-gray-400 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Milestone
                  </button>
                )}
              </div>

              {isMilestonesExpanded && (
                <div className="mt-4">
                  {milestones.length === 0 && !showMilestoneForm ? (
                    <div className="text-center py-8">
                      <svg className="mx-auto h-12 w-12 text-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
                      </svg>
                      <p className="mt-2 text-sm text-text-tertiary">No milestones yet</p>
                      {canEdit && (
                        <button
                          onClick={() => {
                            setMilestoneFormData({ milestone_date: '', description: '' });
                            setEditingMilestoneId(null);
                            setShowMilestoneForm(true);
                          }}
                          className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-surface-primary border border-border-primary rounded-full text-sm font-medium text-text-secondary hover:bg-background-secondary hover:border-gray-400 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Add Milestone
                        </button>
                      )}
                    </div>
                  ) : (
                    <div>
                      {/* Table Header */}
                      <div className="grid grid-cols-12 gap-4 px-4 py-2 border-b border-border-primary">
                        <div className="col-span-3">
                          <span className="text-xs font-semibold text-text-tertiary tracking-wider uppercase">Date</span>
                        </div>
                        <div className={canEdit ? 'col-span-7' : 'col-span-9'}>
                          <span className="text-xs font-semibold text-text-tertiary tracking-wider uppercase">Description</span>
                        </div>
                        {canEdit && (
                          <div className="col-span-2 text-right">
                            <span className="text-xs font-semibold text-text-tertiary tracking-wider uppercase">Actions</span>
                          </div>
                        )}
                      </div>

                      {/* Add Form */}
                      {showMilestoneForm && !editingMilestoneId && (
                        <div className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-border-primary bg-indigo-50/50 dark:bg-indigo-900/10">
                          <div className="col-span-3">
                            <input
                              type="date"
                              value={milestoneFormData.milestone_date}
                              onChange={(e) => setMilestoneFormData({ ...milestoneFormData, milestone_date: e.target.value })}
                              className="w-full px-2 py-1.5 text-sm border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-surface-primary"
                            />
                          </div>
                          <div className="col-span-7">
                            <input
                              type="text"
                              value={milestoneFormData.description}
                              onChange={(e) => setMilestoneFormData({ ...milestoneFormData, description: e.target.value })}
                              placeholder="Milestone description..."
                              className="w-full px-2 py-1.5 text-sm border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-surface-primary"
                            />
                          </div>
                          <div className="col-span-2 flex items-center justify-end gap-2">
                            <button
                              onClick={async () => {
                                if (!token || !id || !milestoneFormData.milestone_date || !milestoneFormData.description.trim()) return;
                                try {
                                  await milestoneService.createMilestone(id, milestoneFormData, token);
                                  setShowMilestoneForm(false);
                                  setMilestoneFormData({ milestone_date: '', description: '' });
                                  loadMilestones();
                                } catch (err) {
                                  console.error('Failed to create milestone:', err);
                                }
                              }}
                              disabled={!milestoneFormData.milestone_date || !milestoneFormData.description.trim()}
                              className="px-2.5 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => {
                                setShowMilestoneForm(false);
                                setMilestoneFormData({ milestone_date: '', description: '' });
                              }}
                              className="px-2.5 py-1.5 text-xs font-medium text-text-secondary bg-surface-primary border border-border-primary rounded-md hover:bg-background-secondary"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Milestone Rows */}
                      <div className="divide-y divide-border-primary">
                        {milestones.map((milestone) => (
                          <div key={milestone.id} className="grid grid-cols-12 gap-4 px-4 py-3 hover:bg-background-secondary/50">
                            {editingMilestoneId === milestone.id ? (
                              <>
                                <div className="col-span-3">
                                  <input
                                    type="date"
                                    value={milestoneFormData.milestone_date}
                                    onChange={(e) => setMilestoneFormData({ ...milestoneFormData, milestone_date: e.target.value })}
                                    className="w-full px-2 py-1.5 text-sm border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-surface-primary"
                                  />
                                </div>
                                <div className="col-span-7">
                                  <input
                                    type="text"
                                    value={milestoneFormData.description}
                                    onChange={(e) => setMilestoneFormData({ ...milestoneFormData, description: e.target.value })}
                                    className="w-full px-2 py-1.5 text-sm border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-surface-primary"
                                  />
                                </div>
                                <div className="col-span-2 flex items-center justify-end gap-2">
                                  <button
                                    onClick={async () => {
                                      if (!token || !id) return;
                                      try {
                                        await milestoneService.updateMilestone(id, milestone.id, milestoneFormData, token);
                                        setEditingMilestoneId(null);
                                        setMilestoneFormData({ milestone_date: '', description: '' });
                                        loadMilestones();
                                      } catch (err) {
                                        console.error('Failed to update milestone:', err);
                                      }
                                    }}
                                    disabled={!milestoneFormData.milestone_date || !milestoneFormData.description.trim()}
                                    className="px-2.5 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingMilestoneId(null);
                                      setMilestoneFormData({ milestone_date: '', description: '' });
                                    }}
                                    className="px-2.5 py-1.5 text-xs font-medium text-text-secondary bg-surface-primary border border-border-primary rounded-md hover:bg-background-secondary"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="col-span-3 flex items-center">
                                  <span className="text-sm text-text-primary">
                                    {new Date(milestone.milestone_date.toString().slice(0, 10) + 'T00:00:00').toLocaleDateString()}
                                  </span>
                                </div>
                                <div className={`${canEdit ? 'col-span-7' : 'col-span-9'} flex items-center`}>
                                  <span className="text-sm text-text-primary">{milestone.description}</span>
                                </div>
                                {canEdit && (
                                  <div className="col-span-2 flex items-center justify-end gap-2">
                                    <button
                                      onClick={() => {
                                        setEditingMilestoneId(milestone.id);
                                        setMilestoneFormData({
                                          milestone_date: milestone.milestone_date.split('T')[0],
                                          description: milestone.description
                                        });
                                        setShowMilestoneForm(false);
                                      }}
                                      className="p-1 text-text-tertiary hover:text-indigo-600 focus:outline-none"
                                      title="Edit milestone"
                                    >
                                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                      </svg>
                                    </button>
                                    <button
                                      onClick={async () => {
                                        if (!token || !id) return;
                                        try {
                                          await milestoneService.deleteMilestone(id, milestone.id, token);
                                          loadMilestones();
                                        } catch (err) {
                                          console.error('Failed to delete milestone:', err);
                                        }
                                      }}
                                      className="p-1 text-text-tertiary hover:text-red-600 focus:outline-none"
                                      title="Delete milestone"
                                    >
                                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    </button>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            )}

            {/* Requirements Overview Card */}
            <div className="bg-surface-primary rounded-lg shadow p-6">
              {/* Collapsible Header */}
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => {
                  const newState = !isRequirementsExpanded;
                  setIsRequirementsExpanded(newState);
                  saveExpandedState('requirements', newState);
                }}
              >
                <div className="flex items-center gap-2">
                  <svg
                    className={`h-5 w-5 text-text-tertiary transition-transform duration-200 ${isRequirementsExpanded ? 'rotate-90' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <h2 className="text-lg font-semibold text-text-primary">Requirements Overview</h2>
                  {requirementCounts.totalCount > 0 && (
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-r from-indigo-100 to-blue-100 border border-indigo-200">
                      <span className="text-sm font-semibold text-indigo-700 dark:text-indigo-400">{requirementCounts.totalCount}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {requirementCounts.totalCount > 0 && (
                    <div className="flex items-center gap-2">
                      <div className="relative group">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-50 dark:bg-green-900/30 border border-green-100 dark:border-green-800 cursor-default">
                          <span className="text-sm font-semibold text-green-700 dark:text-green-400">{requirementCounts.byApprovalStatus.approved}</span>
                        </div>
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-900 dark:bg-gray-800 text-white text-sm rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 whitespace-nowrap pointer-events-none z-50">
                          Approved Requirements
                        </div>
                      </div>
                      <div className="relative group">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-100 dark:border-yellow-800 cursor-default">
                          <span className="text-sm font-semibold text-yellow-700 dark:text-yellow-400">{requirementCounts.byApprovalStatus.pending}</span>
                        </div>
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-900 dark:bg-gray-800 text-white text-sm rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 whitespace-nowrap pointer-events-none z-50">
                          Pending Requirements
                        </div>
                      </div>
                      <div className="relative group">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-red-50 dark:bg-red-900/30 border border-red-100 dark:border-red-800 cursor-default">
                          <span className="text-sm font-semibold text-red-700 dark:text-red-400">{requirementCounts.byApprovalStatus.rejected}</span>
                        </div>
                        <div className="absolute bottom-full right-0 mb-2 px-3 py-1.5 bg-gray-900 dark:bg-gray-800 text-white text-sm rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 whitespace-nowrap pointer-events-none z-50">
                          Rejected Requirements
                        </div>
                      </div>
                    </div>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/projects/${id}/requirements`);
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-surface-primary border border-border-primary rounded-full text-sm font-medium text-text-secondary hover:bg-background-secondary hover:border-gray-400 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    View All
                  </button>
                </div>
              </div>

              {/* Collapsible Content */}
              {isRequirementsExpanded && (
                <div className="mt-6 pt-6 border-t border-border-primary">
                  {/* Total Count */}
                  <div className="mb-4 p-4 border border-border-primary rounded-lg hover:border-indigo-300 dark:hover:border-indigo-500 transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-text-secondary">Total Requirements</p>
                    <p className="text-3xl font-bold text-text-primary mt-1">{requirementCounts.totalCount}</p>
                  </div>
                  <div className="flex items-center justify-center w-14 h-14 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600">
                    <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* By Type */}
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-text-secondary mb-3">By Requirement Type</h3>
                <div className="grid grid-cols-5 gap-3">
                  <div className="p-3 border border-border-primary rounded-lg hover:border-blue-300 dark:hover:border-blue-500 transition-colors">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-text-secondary">Functional</p>
                      <span className="text-lg font-bold text-text-primary">{requirementCounts.byType.functional}</span>
                    </div>
                  </div>
                  <div className="p-3 border border-border-primary rounded-lg hover:border-blue-300 dark:hover:border-blue-500 transition-colors">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-text-secondary">Non-Functional</p>
                      <span className="text-lg font-bold text-text-primary">{requirementCounts.byType['non-functional']}</span>
                    </div>
                  </div>
                  <div className="p-3 border border-border-primary rounded-lg hover:border-blue-300 dark:hover:border-blue-500 transition-colors">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-text-secondary">Technical</p>
                      <span className="text-lg font-bold text-text-primary">{requirementCounts.byType.technical}</span>
                    </div>
                  </div>
                  <div className="p-3 border border-border-primary rounded-lg hover:border-blue-300 dark:hover:border-blue-500 transition-colors">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-text-secondary">Business</p>
                      <span className="text-lg font-bold text-text-primary">{requirementCounts.byType.business}</span>
                    </div>
                  </div>
                  <div className="p-3 border border-border-primary rounded-lg hover:border-indigo-300 dark:hover:border-indigo-500 transition-colors">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-text-secondary">User Story</p>
                      <span className="text-lg font-bold text-text-primary">{requirementCounts.byType['user-story']}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* By Approval Status */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-sm font-semibold text-text-secondary">Approval Status</h3>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 border border-border-primary rounded-lg hover:border-green-300 dark:hover:border-green-500 transition-colors">
                    <div className="text-center">
                      <p className="text-xs text-text-secondary mb-1">Approved</p>
                      <span className="text-xl font-bold text-green-600 dark:text-green-400">{requirementCounts.byApprovalStatus.approved}</span>
                    </div>
                  </div>
                  <div className="p-3 border border-border-primary rounded-lg hover:border-yellow-300 dark:hover:border-yellow-500 transition-colors">
                    <div className="text-center">
                      <p className="text-xs text-text-secondary mb-1">Pending</p>
                      <span className="text-xl font-bold text-yellow-600 dark:text-yellow-400">{requirementCounts.byApprovalStatus.pending}</span>
                    </div>
                  </div>
                  <div className="p-3 border border-border-primary rounded-lg hover:border-red-300 dark:hover:border-red-500 transition-colors">
                    <div className="text-center">
                      <p className="text-xs text-text-secondary mb-1">Rejected</p>
                      <span className="text-xl font-bold text-red-600 dark:text-red-400">{requirementCounts.byApprovalStatus.rejected}</span>
                    </div>
                  </div>
                </div>
              </div>
                </div>
              )}
            </div></>)}

            {/* Tech Stack Card */}
            {!isStakeholder && (<div className="bg-surface-primary rounded-lg shadow p-6">
              {/* Collapsible Header */}
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={handleTechStackExpandToggle}
              >
                <div className="flex items-center gap-2">
                  <svg
                    className={`h-5 w-5 text-text-tertiary transition-transform duration-200 ${isTechStackExpanded ? 'rotate-90' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <h2 className="text-lg font-semibold text-text-primary">Technology Stack</h2>
                  {projectTechStack.length > 0 && (
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-r from-indigo-100 to-blue-100 border border-indigo-200">
                      <span className="text-sm font-semibold text-indigo-700 dark:text-indigo-400">{projectTechStack.length}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Selected Technologies Display */}
              {projectTechStack.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {projectTechStack.map((tech) => (
                    <span
                      key={tech.id}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-indigo-50 text-indigo-700 border border-indigo-200"
                    >
                      {tech.tech_name}
                    </span>
                  ))}
                </div>
              )}

              {/* Collapsible Panel with Checkboxes */}
              {isTechStackExpanded && (
                <div className="mt-4 pt-4 border-t border-border-primary">
                  {canEdit ? (
                    <>
                      <p className="text-sm text-text-secondary mb-4">
                        Select technologies that apply to this project:
                      </p>
                      <div className="grid grid-cols-5 gap-3 max-h-96 overflow-y-auto">
                        {TOP_TECH_STACKS.map((techName) => {
                          const isSelected = projectTechStack.some(tech => {
                            console.log(`Checking ${techName} against ${tech.tech_name}`);
                            return tech.tech_name === techName;
                          });
                          return (
                            <label
                              key={techName}
                              className="flex items-center gap-2 p-2 rounded-md hover:bg-background-secondary cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleToggleTechStack(techName)}
                                className="w-4 h-4 text-indigo-600 dark:text-indigo-400 border-border-primary rounded focus:ring-indigo-500"
                              />
                              <span className="text-sm text-text-primary">{techName}</span>
                            </label>
                          );
                        })}
                      </div>
                      <div className="mt-4 pt-3 border-t border-gray-100 text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddTechStackClick();
                          }}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-surface-primary border border-border-primary rounded-full text-sm font-medium text-text-secondary hover:bg-background-secondary hover:border-gray-400 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Add Technology
                        </button>
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-text-tertiary">
                      {projectTechStack.length === 0 ? 'No technologies added yet' : 'View only mode'}
                    </p>
                  )}
                </div>
              )}
            </div>)}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Team Members Card */}
            {!isStakeholder && (<div className="bg-surface-primary rounded-lg shadow p-6">
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => {
                  const newState = !isTeamMembersExpanded;
                  setIsTeamMembersExpanded(newState);
                  saveExpandedState('teamMembers', newState);
                }}
              >
                <div className="flex items-center gap-2">
                  <svg
                    className={`h-5 w-5 text-text-tertiary transition-transform duration-200 ${isTeamMembersExpanded ? 'rotate-90' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <h2 className="text-lg font-semibold text-text-primary">Team Members</h2>
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-r from-indigo-100 to-blue-100 border border-indigo-200">
                    <span className="text-sm font-semibold text-indigo-700 dark:text-indigo-400">{members.length}</span>
                  </div>
                </div>
                {canManageMembers && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setAddMemberData({ user_id: '', role: 'contributor' });
                      setAddMemberError('');
                      setIsAddingMember(true);
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-surface-primary border border-border-primary rounded-full text-sm font-medium text-text-secondary hover:bg-background-secondary hover:border-gray-400 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add
                  </button>
                )}
              </div>

              {isTeamMembersExpanded && (
                <div className="mt-4 pt-4 border-t border-border-primary space-y-6">
                  {members.length === 0 ? (
                    <p className="text-sm text-text-tertiary">No team members yet</p>
                  ) : (
                    <>
                      {/* Regular Team Members Section */}
                      {members.filter(m => m.role !== 'stakeholder').length > 0 && (
                        <div>
                          <h3 className="text-sm font-semibold text-text-secondary mb-3 uppercase tracking-wide">Team Members</h3>
                          <div className="space-y-3">
                            {members
                              .filter(member => member.role !== 'stakeholder')
                              .map((member) => (
                                <div key={member.id} className="flex items-center justify-between py-2">
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-text-primary">
                                      {member.user_first_name} {member.user_last_name}
                                      {member.is_owner && (
                                        <span className="ml-2 px-2 py-0.5 rounded text-xs bg-indigo-100 text-indigo-800">
                                          Owner
                                        </span>
                                      )}
                                    </p>
                                    <p className="text-xs text-text-tertiary">{member.user_email}</p>
                                    {member.role && (
                                      <p className="text-xs text-text-tertiary capitalize">{member.role.replace('_', ' ')}</p>
                                    )}
                                  </div>
                                  {canManageMembers && !member.is_owner && member.user_id && (
                                    <button
                                      onClick={() => handleRemoveMember(member.user_id!)}
                                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-surface-primary border border-border-primary rounded-full text-sm font-medium text-text-secondary hover:bg-background-secondary hover:border-gray-400 transition-colors"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                      Remove
                                    </button>
                                  )}
                                </div>
                              ))}
                          </div>
                        </div>
                      )}

                      {/* Stakeholders Section */}
                      {members.filter(m => m.role === 'stakeholder').length > 0 && (
                        <div>
                          <h3 className="text-sm font-semibold text-text-secondary mb-3 uppercase tracking-wide">Stakeholders</h3>
                          <div className="space-y-3">
                            {members
                              .filter(member => member.role === 'stakeholder')
                              .map((member) => (
                                <div key={member.id} className="py-2 bg-amber-50 dark:bg-amber-900/20 rounded-md p-3 border border-amber-100 dark:border-amber-800">
                                  {/* Header row with name, badge, and action buttons */}
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <p className="text-sm font-medium text-text-primary">
                                        {member.stakeholder_name || `${member.user_first_name} ${member.user_last_name}`}
                                      </p>
                                      <span className="px-2 py-0.5 rounded text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-400">
                                        Stakeholder
                                      </span>
                                    </div>
                                    {canManageMembers && (
                                      <div className="flex items-center gap-2">
                                        <button
                                          onClick={() => handleEditStakeholderClick(member)}
                                          className="inline-flex items-center gap-2 px-3 py-1.5 bg-surface-primary border border-border-primary rounded-full text-sm font-medium text-text-secondary hover:bg-background-secondary hover:border-gray-400 transition-colors"
                                        >
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                          </svg>
                                          Edit
                                        </button>
                                        <button
                                          onClick={() => handleRemoveStakeholder(member.id, member.stakeholder_name || '')}
                                          className="inline-flex items-center gap-2 px-3 py-1.5 bg-surface-primary border border-border-primary rounded-full text-sm font-medium text-text-secondary hover:bg-background-secondary hover:border-gray-400 transition-colors"
                                        >
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                          </svg>
                                          Remove
                                        </button>
                                      </div>
                                    )}
                                  </div>

                                  {/* Responsibility section - full width */}
                                  {member.stakeholder_responsibility && (
                                    <div>
                                      <p className="text-xs font-medium text-text-secondary">Responsibility:</p>
                                      <p className="text-xs text-text-secondary mt-0.5">{member.stakeholder_responsibility}</p>
                                    </div>
                                  )}
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>)}

            {/* Personas Card */}
            {!isStakeholder && (<div className="bg-surface-primary rounded-lg shadow p-6">
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => {
                  const newState = !isPersonasExpanded;
                  setIsPersonasExpanded(newState);
                  saveExpandedState('personas', newState);
                }}
              >
                <div className="flex items-center gap-2">
                  <svg
                    className={`h-5 w-5 text-text-tertiary transition-transform duration-200 ${isPersonasExpanded ? 'rotate-90' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <h2 className="text-lg font-semibold text-text-primary">Personas</h2>
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-r from-purple-100 to-pink-100 border border-purple-200">
                    <span className="text-sm font-semibold text-purple-700 dark:text-purple-400">{personas.length}</span>
                  </div>
                </div>
                {canEdit && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddPersonaClick();
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-surface-primary border border-border-primary rounded-full text-sm font-medium text-text-secondary hover:bg-background-secondary hover:border-gray-400 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add
                  </button>
                )}
              </div>

              {isPersonasExpanded && (
                <div className="mt-4 pt-4 border-t border-border-primary space-y-3">
                {personas.length === 0 ? (
                  <p className="text-sm text-text-tertiary">No personas yet</p>
                ) : (
                  personas.map((persona) => (
                    <div key={persona.id} className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
                      <img
                        src={personaService.getAvatarImagePath(persona.avatar_id)}
                        alt={persona.name}
                        className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-text-primary truncate">{persona.name}</p>
                        {persona.role && (
                          <p className="text-xs text-text-tertiary truncate">{persona.role}</p>
                        )}
                        {persona.description && (
                          <p className="text-xs text-text-secondary mt-1 line-clamp-2">{persona.description}</p>
                        )}
                        {(persona.age_range || persona.gender || persona.location) && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {persona.age_range && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:text-blue-400">
                                {persona.age_range}
                              </span>
                            )}
                            {persona.gender && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                {persona.gender}
                              </span>
                            )}
                            {persona.location && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:text-green-400">
                                📍 {persona.location}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button
                          onClick={() => handleViewPersonaClick(persona)}
                          className="inline-flex items-center gap-2 px-3 py-1.5 bg-surface-primary border border-border-primary rounded-full text-sm font-medium text-text-secondary hover:bg-background-secondary hover:border-gray-400 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          View
                        </button>
                        {canEdit && (
                          <button
                            onClick={() => handleDeletePersona(persona)}
                            className="inline-flex items-center gap-2 px-3 py-1.5 bg-surface-primary border border-border-primary rounded-full text-sm font-medium text-text-secondary hover:bg-background-secondary hover:border-gray-400 transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
                </div>
              )}
            </div>)}

            {/* Reports Card */}
            <div className="bg-surface-primary rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <h2 className="text-lg font-semibold text-text-primary">Reports</h2>
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-r from-indigo-100 to-blue-100 border border-indigo-200">
                    <svg className="w-4 h-4 text-indigo-700 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                {/* Executive Summary Report Card - stakeholders only see if content exists */}
                {(!isStakeholder || project?.executive_summary) && (
                <div
                  onClick={() => setIsExecutiveSummaryModalOpen(true)}
                  className="group flex items-center justify-between py-3 px-4 border border-border-primary rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-all cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center text-white group-hover:scale-110 transition-transform">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-text-primary group-hover:text-indigo-700 transition-colors">
                        Executive Summary
                      </h3>
                      {project?.executive_summary ? (
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              project.executive_summary_method === 'ai'
                                ? 'bg-blue-100 text-blue-800 dark:text-blue-400'
                                : 'bg-green-100 text-green-800 dark:text-green-400'
                            }`}>
                              {project.executive_summary_method === 'ai' ? 'AI Generated' : 'Manual'}
                            </span>
                            {project.executive_summary_generated_at && (
                              <span className="text-xs text-text-tertiary">
                                {new Date(project.executive_summary_generated_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                          {project.executive_summary_generated_by_name && (
                            <p className="text-xs text-text-tertiary">
                              Created by {project.executive_summary_generated_by_name}
                            </p>
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-text-tertiary mt-0.5">Not created yet</p>
                      )}
                    </div>
                  </div>
                  <svg className="w-5 h-5 text-text-tertiary group-hover:text-indigo-600 dark:text-indigo-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
                )}

                {/* Business Requirements Document Report Card - stakeholders only see if content exists */}
                {(!isStakeholder || project?.brd_content) && (
                <div
                  onClick={() => setIsBRDModalOpen(true)}
                  className="group flex items-center justify-between py-3 px-4 border border-border-primary rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-all cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center text-white group-hover:scale-110 transition-transform">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-text-primary group-hover:text-indigo-700 transition-colors">
                        Business Requirements Document
                      </h3>
                      {project?.brd_content ? (
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              project.brd_method === 'ai'
                                ? 'bg-blue-100 text-blue-800 dark:text-blue-400'
                                : 'bg-green-100 text-green-800 dark:text-green-400'
                            }`}>
                              {project.brd_method === 'ai' ? 'AI Generated' : 'Manual'}
                            </span>
                            {project.brd_generated_at && (
                              <span className="text-xs text-text-tertiary">
                                {new Date(project.brd_generated_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-text-tertiary mt-0.5">Not created yet</p>
                      )}
                    </div>
                  </div>
                  <svg className="w-5 h-5 text-text-tertiary group-hover:text-indigo-600 dark:text-indigo-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
                )}

                {/* Requirements Insights Report Card - hidden for stakeholders */}
                {!isStakeholder && (
                <div
                  onClick={() => {
                    if (requirements.length === 0) {
                      setError('Project must have at least one requirement to view insights');
                      setTimeout(() => setError(''), 5000);
                      return;
                    }
                    handleShowRequirementsInsights();
                  }}
                  className={`group flex items-center justify-between py-3 px-4 border border-border-primary rounded-lg ${
                    requirements.length === 0
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:border-purple-300 hover:bg-purple-50 cursor-pointer'
                  } transition-all`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center text-white group-hover:scale-110 transition-transform">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-text-primary group-hover:text-purple-700 transition-colors">
                        Requirements Insights
                      </h3>
                      <p className="text-xs text-text-tertiary mt-0.5">
                        AI insights for {requirements.length} requirement{requirements.length !== 1 ? 's' : ''}
                      </p>
                      {project?.requirements_insights_generation_count !== undefined && (
                        <p className={`text-xs mt-1 ${
                          (project.requirements_insights_generation_count || 0) >= 5
                            ? 'text-red-600 dark:text-red-400 font-medium'
                            : (project.requirements_insights_generation_count || 0) >= 4
                              ? 'text-orange-600'
                              : 'text-text-tertiary'
                        }`}>
                          {project.requirements_insights_generation_count || 0}/5 regenerations used
                        </p>
                      )}
                    </div>
                  </div>
                  <svg className="w-5 h-5 text-text-tertiary group-hover:text-purple-600 dark:text-purple-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
                )}

                {/* Empty state for stakeholders when no reports are generated */}
                {isStakeholder && !project?.executive_summary && !project?.brd_content && (
                  <div className="text-center py-6">
                    <svg className="mx-auto h-12 w-12 text-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-text-primary">No reports available</h3>
                    <p className="mt-1 text-sm text-text-tertiary">No reports have been generated for this project yet.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Project Approval Card */}
            <div className="bg-surface-primary rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <h2 className="text-lg font-semibold text-text-primary">Project Approval</h2>
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-r from-green-100 to-emerald-100 border border-green-200">
                    <svg className="w-4 h-4 text-green-700 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                {approvalSummary && approvalSummary.total_stakeholders > 0 && (
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    approvalSummary.fully_approved_stakeholders === approvalSummary.total_stakeholders
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-400'
                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-400'
                  }`}>
                    {isStakeholder
                      ? (() => {
                          const myApprovals = approvalSummary.approvals.filter(a => a.user_id === currentUser?.id);
                          const myApproved = myApprovals.filter(a => a.is_approved).length;
                          return `${myApproved}/${myApprovals.length} approved`;
                        })()
                      : `${approvalSummary.fully_approved_stakeholders}/${approvalSummary.total_stakeholders} stakeholders`}
                  </span>
                )}
              </div>

              <div
                onClick={() => setIsProjectApprovalModalOpen(true)}
                className="group flex items-center justify-between py-3 px-4 border border-border-primary rounded-lg hover:border-green-300 hover:bg-green-50 transition-all cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center text-white group-hover:scale-110 transition-transform">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-text-primary group-hover:text-green-700 transition-colors">
                      {isStakeholder ? 'Approve Project' : 'View Approvals'}
                    </h3>
                    <p className="text-xs text-text-tertiary mt-0.5">
                      {isStakeholder
                        ? 'Review and sign off on deliverables'
                        : approvalSummary && approvalSummary.total_stakeholders > 0
                          ? `${approvalSummary.fully_approved_stakeholders} of ${approvalSummary.total_stakeholders} fully approved`
                          : 'No stakeholder approvals yet'}
                    </p>
                  </div>
                </div>
                <svg className="w-5 h-5 text-text-tertiary group-hover:text-green-600 dark:text-green-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Executive Summary Modal */}
      <ExecutiveSummaryModal
        isOpen={isExecutiveSummaryModalOpen}
        onClose={() => setIsExecutiveSummaryModalOpen(false)}
        projectId={id || ''}
        projectName={project?.name || ''}
        token={token || ''}
        existingSummary={project?.executive_summary}
        existingMethod={project?.executive_summary_method}
        existingGeneratedAt={project?.executive_summary_generated_at}
        existingTokensUsed={project?.executive_summary_tokens_used}
        existingModel={project?.executive_summary_model}
        existingGeneratedBy={project?.executive_summary_generated_by_name || undefined}
        currentUserName={currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : undefined}
        onSummaryUpdated={loadProject}
        readOnly={!canEditReports}
        startDate={project?.start_date}
        targetCompletionDate={project?.target_completion_date}
      />

      {/* BRD Modal */}
      <BRDModal
        isOpen={isBRDModalOpen}
        onClose={() => setIsBRDModalOpen(false)}
        projectId={id || ''}
        projectName={project?.name || ''}
        companyName={company?.name}
        token={token || ''}
        existingBRD={project?.brd_content}
        existingMethod={project?.brd_method}
        existingGeneratedAt={project?.brd_generated_at}
        existingTokensUsed={project?.brd_tokens_used}
        existingModel={project?.brd_model}
        existingGeneratedBy={project?.brd_generated_by_name || undefined}
        currentUserName={currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : undefined}
        onBRDUpdated={loadProject}
        readOnly={!canEditReports}
        startDate={project?.start_date}
        targetCompletionDate={project?.target_completion_date}
        requirements={requirements}
        milestones={milestones}
      />

      {/* Requirements Insights Modal */}
      <RequirementsReviewModal
        isOpen={isRequirementsReviewModalOpen}
        onClose={() => setIsRequirementsReviewModalOpen(false)}
        projectName={project?.name || ''}
        reviewData={requirementsReviewData}
        onRegenerate={handleGenerateRequirementsReview}
        onGenerateNew={handleGenerateNewRequirementsInsights}
        isGenerating={isGeneratingRequirementsReview}
        requirementsCount={requirements.length}
        generationCount={generationCount}
        requirementsWithoutInsightsCount={requirementsWithoutInsightsCount}
        readOnly={!canEditReports}
      />

      {/* Project Approval Modal */}
      <ProjectApprovalModal
        isOpen={isProjectApprovalModalOpen}
        onClose={() => setIsProjectApprovalModalOpen(false)}
        projectId={id || ''}
        token={token || ''}
        isStakeholder={!!isStakeholder}
        currentUserId={currentUser?.id || ''}
        onApprovalUpdated={loadApprovalSummary}
      />

      {/* Edit Project Modal */}
      {isEditing && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-black/60 backdrop-blur-sm" onClick={() => { setIsEditing(false); setEditError(''); }} />
            <div className="inline-block align-bottom bg-surface-primary rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-surface-primary px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg font-medium text-text-primary mb-4">Edit Project</h3>
                {editError && (
                  <div className="mb-4 p-4 rounded-md bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800">
                    <p className="text-sm text-red-800 dark:text-red-400">{editError}</p>
                  </div>
                )}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Project Name</label>
                    <input
                      type="text"
                      value={editFormData.name || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Project Code</label>
                    <input
                      type="text"
                      value={editFormData.project_code || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, project_code: e.target.value })}
                      className="w-full px-3 py-2 border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Description</label>
                    <RichTextEditor
                      value={editFormData.description || ''}
                      onChange={(html) => setEditFormData({ ...editFormData, description: html })}
                      placeholder="Enter project description..."
                      minHeight="80px"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Project Type</label>
                    <input
                      type="text"
                      value={projectTypeSearch}
                      onChange={(e) => setProjectTypeSearch(e.target.value)}
                      placeholder="Search project types..."
                      className="w-full px-3 py-2 mb-2 border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <select
                      value={editFormData.project_type_id || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, project_type_id: e.target.value })}
                      className="w-full px-3 py-2 border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">-- Select a project type --</option>
                      {projectTypes
                        .filter(type =>
                          projectTypeSearch === '' ||
                          type.name.toLowerCase().includes(projectTypeSearch.toLowerCase()) ||
                          type.category.toLowerCase().includes(projectTypeSearch.toLowerCase())
                        )
                        .map((type) => (
                          <option key={type.id} value={type.id}>{type.name} ({type.category})</option>
                        ))}
                    </select>
                    <p className="mt-1 text-xs text-text-tertiary">Optional: Search and select a project type{projectTypeSearch && ` (${projectTypes.filter(type =>
                      projectTypeSearch === '' ||
                      type.name.toLowerCase().includes(projectTypeSearch.toLowerCase()) ||
                      type.category.toLowerCase().includes(projectTypeSearch.toLowerCase())
                    ).length} results)`}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Status</label>
                    <select
                      value={editFormData.status || 'draft'}
                      onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value as any })}
                      className="w-full px-3 py-2 border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="draft">Draft</option>
                      <option value="active">Active</option>
                      <option value="on_hold">On Hold</option>
                      <option value="completed">Completed</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Start Date</label>
                    <input
                      type="date"
                      value={editFormData.start_date || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, start_date: e.target.value })}
                      className="w-full px-3 py-2 border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Target Completion Date</label>
                    <input
                      type="date"
                      value={editFormData.target_completion_date || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, target_completion_date: e.target.value })}
                      className="w-full px-3 py-2 border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Tags</label>
                    <input
                      type="text"
                      value={editFormData.tags || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, tags: e.target.value })}
                      placeholder="Comma-separated tags"
                      className="w-full px-3 py-2 border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>
              <div className="bg-background-secondary px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleEditProject}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-gradient-to-r from-indigo-600 to-blue-600 text-base font-medium text-white hover:from-indigo-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Save Changes
                </button>
                <button
                  onClick={() => { setIsEditing(false); setEditError(''); }}
                  className="mt-3 w-full inline-flex justify-center rounded-full border border-border-primary shadow-sm px-4 py-2 bg-surface-primary text-base font-medium text-text-secondary hover:bg-background-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Type Selection Modal */}
      {showAddTypeSelection && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-black/60 backdrop-blur-sm" onClick={() => setShowAddTypeSelection(false)} />
            <div className="inline-block align-bottom bg-surface-primary rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-surface-primary px-4 pt-5 pb-4 sm:p-6">
                <h3 className="text-lg font-medium text-text-primary mb-6 text-center">What would you like to add?</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Team Member Card */}
                  <button
                    onClick={() => {
                      setShowAddTypeSelection(false);
                      setIsAddingMember(true);
                    }}
                    className="p-6 border-2 border-indigo-500 rounded-lg shadow-md hover:shadow-lg transition-all text-left bg-gradient-to-br from-indigo-50 to-blue-50"
                  >
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-indigo-100 mb-4">
                      <svg className="w-6 h-6 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    </div>
                    <h4 className="text-lg font-semibold text-text-primary mb-2">
                      Team Member
                      <span className="ml-2 px-2 py-0.5 text-xs font-medium text-indigo-700 bg-indigo-100 rounded-full">
                        Recommended
                      </span>
                    </h4>
                    <p className="text-sm text-text-secondary">
                      Add an existing user from your company to this project as a team member with specific permissions.
                    </p>
                  </button>

                  {/* Stakeholder Card */}
                  <button
                    onClick={() => {
                      setShowAddTypeSelection(false);
                      setIsAddingStakeholder(true);
                    }}
                    className="p-6 border-2 border-border-primary rounded-lg hover:border-indigo-500 hover:shadow-md transition-all text-left"
                  >
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-background-tertiary mb-4">
                      <svg className="w-6 h-6 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h4 className="text-lg font-semibold text-text-primary mb-2">Stakeholder</h4>
                    <p className="text-sm text-text-secondary">
                      Add an external stakeholder who doesn't need system access but should be tracked for this project.
                    </p>
                  </button>
                </div>
                <div className="mt-6 flex justify-center">
                  <button
                    onClick={() => setShowAddTypeSelection(false)}
                    className="px-4 py-2 border border-border-primary rounded-full text-sm font-medium text-text-secondary bg-surface-primary hover:bg-background-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      {isAddingMember && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-black/60 backdrop-blur-sm" onClick={() => { setIsAddingMember(false); setAddMemberError(''); }} />
            <div className="inline-block align-bottom bg-surface-primary rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-surface-primary px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg font-medium text-text-primary mb-4">Add Team Member</h3>
                {addMemberError && (
                  <div className="mb-4 p-4 rounded-md bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800">
                    <p className="text-sm text-red-800 dark:text-red-400">{addMemberError}</p>
                  </div>
                )}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Select User</label>
                    <select
                      value={addMemberData.user_id}
                      onChange={(e) => setAddMemberData({ ...addMemberData, user_id: e.target.value })}
                      className="w-full px-3 py-2 border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">-- Select a user --</option>
                      {availableUsers.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.first_name} {user.last_name} ({user.email})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="bg-background-secondary px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleAddMember}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-gradient-to-r from-indigo-600 to-blue-600 text-base font-medium text-white hover:from-indigo-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Add Member
                </button>
                <button
                  onClick={() => { setIsAddingMember(false); setAddMemberError(''); }}
                  className="mt-3 w-full inline-flex justify-center rounded-full border border-border-primary shadow-sm px-4 py-2 bg-surface-primary text-base font-medium text-text-secondary hover:bg-background-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Stakeholder Modal */}
      {isAddingStakeholder && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-black/60 backdrop-blur-sm" onClick={() => { setIsAddingStakeholder(false); setAddStakeholderError(''); }} />
            <div className="inline-block align-bottom bg-surface-primary rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-surface-primary px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg font-medium text-text-primary mb-4">Add Stakeholder</h3>
                {addStakeholderError && (
                  <div className="mb-4 p-4 rounded-md bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800">
                    <p className="text-sm text-red-800 dark:text-red-400">{addStakeholderError}</p>
                  </div>
                )}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      Stakeholder Name
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="text"
                      value={addStakeholderData.stakeholder_name}
                      onChange={(e) => setAddStakeholderData({ ...addStakeholderData, stakeholder_name: e.target.value })}
                      className="w-full px-3 py-2 border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Enter stakeholder name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      Responsibility
                    </label>
                    <textarea
                      value={addStakeholderData.stakeholder_responsibility || ''}
                      onChange={(e) => setAddStakeholderData({ ...addStakeholderData, stakeholder_responsibility: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Describe the stakeholder's responsibility..."
                    />
                    <p className="mt-1 text-sm text-text-tertiary">
                      Brief description of what this stakeholder is responsible for in this project.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-background-secondary px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleAddStakeholder}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-gradient-to-r from-indigo-600 to-blue-600 text-base font-medium text-white hover:from-indigo-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Add Stakeholder
                </button>
                <button
                  onClick={() => { setIsAddingStakeholder(false); setAddStakeholderError(''); }}
                  className="mt-3 w-full inline-flex justify-center rounded-full border border-border-primary shadow-sm px-4 py-2 bg-surface-primary text-base font-medium text-text-secondary hover:bg-background-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Stakeholder Modal */}
      {isEditingStakeholder && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-black/60 backdrop-blur-sm" onClick={() => { setIsEditingStakeholder(false); setEditStakeholderError(''); }} />
            <div className="inline-block align-bottom bg-surface-primary rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-surface-primary px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg font-medium text-text-primary mb-4">Edit Stakeholder</h3>
                {editStakeholderError && (
                  <div className="mb-4 p-4 rounded-md bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800">
                    <p className="text-sm text-red-800 dark:text-red-400">{editStakeholderError}</p>
                  </div>
                )}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      Stakeholder Name
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="text"
                      value={editStakeholderData.stakeholder_name}
                      onChange={(e) => setEditStakeholderData({ ...editStakeholderData, stakeholder_name: e.target.value })}
                      className="w-full px-3 py-2 border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Enter stakeholder name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      Responsibility
                    </label>
                    <textarea
                      value={editStakeholderData.stakeholder_responsibility || ''}
                      onChange={(e) => setEditStakeholderData({ ...editStakeholderData, stakeholder_responsibility: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Describe the stakeholder's responsibility..."
                    />
                    <p className="mt-1 text-sm text-text-tertiary">
                      Brief description of what this stakeholder is responsible for in this project.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-background-secondary px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleEditStakeholder}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-gradient-to-r from-indigo-600 to-blue-600 text-base font-medium text-white hover:from-indigo-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Save Changes
                </button>
                <button
                  onClick={() => { setIsEditingStakeholder(false); setEditStakeholderError(''); }}
                  className="mt-3 w-full inline-flex justify-center rounded-full border border-border-primary shadow-sm px-4 py-2 bg-surface-primary text-base font-medium text-text-secondary hover:bg-background-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-black/60 backdrop-blur-sm" onClick={() => {
              setShowDeleteConfirm(false);
              setDeleteConfirmationInput('');
              setDeleteError('');
            }} />
            <div className="inline-block align-bottom bg-surface-primary rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-surface-primary px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full sm:mx-0 sm:h-10 sm:w-10 ${deleteError ? 'bg-red-100 dark:bg-red-900/30' : 'bg-red-100'}`}>
                    <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-text-primary">Delete Project</h3>
                    <div className="mt-2">
                      {deleteError && (
                        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                          <div className="flex items-start gap-2">
                            <svg className="h-5 w-5 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                            </svg>
                            <p className="text-sm text-red-800 dark:text-red-300">{deleteError}</p>
                          </div>
                        </div>
                      )}
                      <p className="text-sm text-text-tertiary mb-4">
                        This action will permanently delete the project and cannot be undone. All project data will be removed from the system.
                      </p>
                      <p className="text-sm text-text-secondary mb-2">
                        To confirm deletion, please type the project name:
                      </p>
                      <p className="text-sm font-semibold text-text-primary mb-3 bg-background-tertiary px-3 py-2 rounded border border-border-primary">
                        {project.name}
                      </p>
                      <input
                        type="text"
                        value={deleteConfirmationInput}
                        onChange={(e) => setDeleteConfirmationInput(e.target.value)}
                        placeholder="Type project name to confirm"
                        className="w-full px-3 py-2 border border-border-primary rounded-md shadow-sm focus:ring-red-500 focus:border-red-500"
                        autoFocus
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-background-secondary px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleDeleteProject}
                  disabled={deleteConfirmationInput !== project.name}
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm ${
                    deleteConfirmationInput === project.name
                      ? 'bg-red-600 hover:bg-red-700 cursor-pointer'
                      : 'bg-gray-300 cursor-not-allowed'
                  }`}
                >
                  Delete Project
                </button>
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteConfirmationInput('');
                    setDeleteError('');
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-full border border-border-primary shadow-sm px-4 py-2 bg-surface-primary text-base font-medium text-text-secondary hover:bg-background-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Persona Confirmation Modal */}
      {showPersonaDeleteConfirm && personaToDelete && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-black/60 backdrop-blur-sm" onClick={() => { setShowPersonaDeleteConfirm(false); setPersonaToDelete(null); }} />
            <div className="inline-block align-bottom bg-surface-primary rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-surface-primary px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-text-primary">Remove Persona</h3>
                    <div className="mt-2">
                      <p className="text-sm text-text-tertiary">
                        Are you sure you want to permanently remove the persona <span className="font-semibold text-text-primary">"{personaToDelete.name}"</span>? This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-background-secondary px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={confirmDeletePersona}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Remove Persona
                </button>
                <button
                  onClick={() => {
                    setShowPersonaDeleteConfirm(false);
                    setPersonaToDelete(null);
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-full border border-border-primary shadow-sm px-4 py-2 bg-surface-primary text-base font-medium text-text-secondary hover:bg-background-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Remove Stakeholder Confirmation Modal */}
      {showStakeholderRemoveConfirm && stakeholderToRemove && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-black/60 backdrop-blur-sm" onClick={() => { setShowStakeholderRemoveConfirm(false); setStakeholderToRemove(null); }} />
            <div className="inline-block align-bottom bg-surface-primary rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-surface-primary px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-text-primary">Remove Stakeholder</h3>
                    <div className="mt-2">
                      <p className="text-sm text-text-tertiary">
                        Are you sure you want to remove <span className="font-semibold text-text-primary">"{stakeholderToRemove.name}"</span> from this project? This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-background-secondary px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={confirmRemoveStakeholder}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Remove Stakeholder
                </button>
                <button
                  onClick={() => {
                    setShowStakeholderRemoveConfirm(false);
                    setStakeholderToRemove(null);
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-full border border-border-primary shadow-sm px-4 py-2 bg-surface-primary text-base font-medium text-text-secondary hover:bg-background-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Tech Stack Modal */}
      {isAddingTechStack && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-black/60 backdrop-blur-sm" onClick={() => { setIsAddingTechStack(false); setAddTechStackError(''); }} />
            <div className="inline-block align-bottom bg-surface-primary rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-surface-primary px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg font-medium text-text-primary mb-4">Add Technology</h3>
                {addTechStackError && (
                  <div className="mb-4 p-4 rounded-md bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800">
                    <p className="text-sm text-red-800 dark:text-red-400">{addTechStackError}</p>
                  </div>
                )}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Select Technology</label>
                    <input
                      type="text"
                      value={techStackSearch}
                      onChange={(e) => setTechStackSearch(e.target.value)}
                      placeholder="Search technologies..."
                      className="w-full px-3 py-2 mb-2 border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <select
                      value={selectedTechStackId}
                      onChange={(e) => setSelectedTechStackId(e.target.value)}
                      className="w-full px-3 py-2 border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      size={8}
                    >
                      <option value="">-- Select a technology --</option>
                      {availableTechStack
                        .filter(tech => !projectTechStack.some(pt => pt.tech_stack_item_id === tech.id))
                        .filter(tech =>
                          techStackSearch === '' ||
                          tech.name.toLowerCase().includes(techStackSearch.toLowerCase()) ||
                          tech.category.toLowerCase().includes(techStackSearch.toLowerCase()) ||
                          (tech.subcategory && tech.subcategory.toLowerCase().includes(techStackSearch.toLowerCase()))
                        )
                        .map((tech) => (
                          <option key={tech.id} value={tech.id}>
                            {tech.name} ({tech.category}{tech.subcategory ? ` - ${tech.subcategory}` : ''})
                          </option>
                        ))}
                    </select>
                    <p className="mt-1 text-xs text-text-tertiary">Search and select from {availableTechStack.filter(tech => !projectTechStack.some(pt => pt.tech_stack_item_id === tech.id)).length} available technologies</p>
                  </div>
                </div>
              </div>
              <div className="bg-background-secondary px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleAddTechStack}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-gradient-to-r from-indigo-600 to-blue-600 text-base font-medium text-white hover:from-indigo-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Add Technology
                </button>
                <button
                  onClick={() => { setIsAddingTechStack(false); setAddTechStackError(''); }}
                  className="mt-3 w-full inline-flex justify-center rounded-full border border-border-primary shadow-sm px-4 py-2 bg-surface-primary text-base font-medium text-text-secondary hover:bg-background-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Persona Modal */}
      {isPersonaModalOpen && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-surface-primary rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
              <div>
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-lg leading-6 font-medium text-text-primary">
                    {viewingPersona ? 'View Persona' : 'Add Persona'}
                  </h3>
                  {viewingPersona && !isPersonaEditMode && (
                    <img
                      src={personaService.getAvatarImagePath(personaFormData.avatar_id)}
                      alt={personaFormData.name}
                      className="w-32 h-32 rounded-full object-cover"
                    />
                  )}
                </div>

                {/* Generate via AI Button */}
                {!viewingPersona && !isPersonaEditMode && (
                  <div className="mb-4">
                    <button
                      onClick={handleGeneratePersonaWithAI}
                      disabled={isGeneratingPersona}
                      className="w-full flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
                    >
                      {isGeneratingPersona ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Generating Persona with AI...
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                          </svg>
                          Generate via AI
                        </>
                      )}
                    </button>
                    <p className="mt-2 text-xs text-text-tertiary text-center">
                      AI will generate a persona based on your project requirements. You can edit any field and select your preferred avatar.
                    </p>
                  </div>
                )}

                {personaError && (
                  <div className="mb-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 px-4 py-3 rounded relative">
                    {personaError}
                  </div>
                )}

                <div className="space-y-4">
                  {/* Name Field */}
                  <div>
                    <label htmlFor="persona-name" className="block text-sm font-medium text-text-secondary mb-1">
                      Name {(!viewingPersona || isPersonaEditMode) && <span className="text-red-500">*</span>}
                    </label>
                    {viewingPersona && !isPersonaEditMode ? (
                      <p className="mt-1 text-sm text-text-primary">{personaFormData.name}</p>
                    ) : (
                      <input
                        type="text"
                        id="persona-name"
                        value={personaFormData.name}
                        onChange={(e) => setPersonaFormData({ ...personaFormData, name: e.target.value })}
                        className="block w-full rounded-md border-border-primary shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 border"
                        placeholder="Enter persona name"
                      />
                    )}
                  </div>

                  {/* Role and Location Fields */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="persona-role" className="block text-sm font-medium text-text-secondary mb-1">
                        Role
                      </label>
                      {viewingPersona && !isPersonaEditMode ? (
                        <p className="mt-1 text-sm text-text-primary">{personaFormData.role || <span className="text-text-tertiary">Not specified</span>}</p>
                      ) : (
                        <input
                          type="text"
                          id="persona-role"
                          value={personaFormData.role}
                          onChange={(e) => setPersonaFormData({ ...personaFormData, role: e.target.value })}
                          className="block w-full rounded-md border-border-primary shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 border"
                          placeholder="e.g., Product Manager, Developer"
                        />
                      )}
                    </div>

                    <div>
                      <label htmlFor="persona-location" className="block text-sm font-medium text-text-secondary mb-1">
                        Location
                      </label>
                      {viewingPersona && !isPersonaEditMode ? (
                        <p className="mt-1 text-sm text-text-primary">{personaFormData.location || <span className="text-text-tertiary">Not specified</span>}</p>
                      ) : (
                        <input
                          type="text"
                          id="persona-location"
                          value={personaFormData.location}
                          onChange={(e) => setPersonaFormData({ ...personaFormData, location: e.target.value })}
                          className="block w-full rounded-md border-border-primary shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 border"
                          placeholder="e.g., New York, USA or London, UK"
                        />
                      )}
                    </div>
                  </div>

                  {/* Description Field */}
                  <div>
                    <label htmlFor="persona-description" className="block text-sm font-medium text-text-secondary mb-1">
                      Description
                    </label>
                    {viewingPersona && !isPersonaEditMode ? (
                      <p className="mt-1 text-sm text-text-primary whitespace-pre-wrap">{personaFormData.description || <span className="text-text-tertiary">Not specified</span>}</p>
                    ) : (
                      <textarea
                        id="persona-description"
                        value={personaFormData.description}
                        onChange={(e) => setPersonaFormData({ ...personaFormData, description: e.target.value })}
                        rows={3}
                        className="block w-full rounded-md border-border-primary shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 border"
                        placeholder="Brief description of this persona"
                      />
                    )}
                  </div>

                  {/* Goals Field */}
                  <div>
                    <label htmlFor="persona-goals" className="block text-sm font-medium text-text-secondary mb-1">
                      Goals
                    </label>
                    {viewingPersona && !isPersonaEditMode ? (
                      <p className="mt-1 text-sm text-text-primary whitespace-pre-wrap">{personaFormData.goals || <span className="text-text-tertiary">Not specified</span>}</p>
                    ) : (
                      <textarea
                        id="persona-goals"
                        value={personaFormData.goals}
                        onChange={(e) => setPersonaFormData({ ...personaFormData, goals: e.target.value })}
                        rows={3}
                        className="block w-full rounded-md border-border-primary shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 border"
                        placeholder="What are this persona's goals?"
                      />
                    )}
                  </div>

                  {/* Pain Points Field */}
                  <div>
                    <label htmlFor="persona-pain-points" className="block text-sm font-medium text-text-secondary mb-1">
                      Pain Points
                    </label>
                    {viewingPersona && !isPersonaEditMode ? (
                      <p className="mt-1 text-sm text-text-primary whitespace-pre-wrap">{personaFormData.pain_points || <span className="text-text-tertiary">Not specified</span>}</p>
                    ) : (
                      <textarea
                        id="persona-pain-points"
                        value={personaFormData.pain_points}
                        onChange={(e) => setPersonaFormData({ ...personaFormData, pain_points: e.target.value })}
                        rows={3}
                        className="block w-full rounded-md border-border-primary shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 border"
                        placeholder="What challenges does this persona face?"
                      />
                    )}
                  </div>

                  {/* Age Range and Gender Fields */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="persona-age-range" className="block text-sm font-medium text-text-secondary mb-1">
                        Age Range
                      </label>
                      {viewingPersona && !isPersonaEditMode ? (
                        <p className="mt-1 text-sm text-text-primary">{personaFormData.age_range || <span className="text-text-tertiary">Not specified</span>}</p>
                      ) : (
                        <select
                          id="persona-age-range"
                          value={personaFormData.age_range}
                          onChange={(e) => setPersonaFormData({ ...personaFormData, age_range: e.target.value })}
                          className="block w-full rounded-md border-border-primary shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 border"
                        >
                          <option value="">Select age range...</option>
                          {personaService.AGE_RANGE_OPTIONS.map((range) => (
                            <option key={range} value={range}>
                              {range}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>

                    <div>
                      <label htmlFor="persona-gender" className="block text-sm font-medium text-text-secondary mb-1">
                        Gender
                      </label>
                      {viewingPersona && !isPersonaEditMode ? (
                        <p className="mt-1 text-sm text-text-primary">{personaFormData.gender || <span className="text-text-tertiary">Not specified</span>}</p>
                      ) : (
                        <select
                          id="persona-gender"
                          value={personaFormData.gender}
                          onChange={(e) => setPersonaFormData({ ...personaFormData, gender: e.target.value })}
                          className="block w-full rounded-md border-border-primary shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 border"
                        >
                          <option value="">Select gender...</option>
                          {personaService.GENDER_OPTIONS.map((gender) => (
                            <option key={gender} value={gender}>
                              {gender}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>

                  {/* Avatar Selection - Only show when creating or editing */}
                  {(!viewingPersona || isPersonaEditMode) && (
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">
                        Select Avatar <span className="text-red-500">*</span>
                      </label>
                      <div className="grid grid-cols-5 gap-3">
                        {personaService.AVATAR_OPTIONS.map((avatar) => (
                          <button
                            key={avatar.id}
                            type="button"
                            onClick={() => setPersonaFormData({ ...personaFormData, avatar_id: avatar.id })}
                            className={`flex items-center justify-center p-2 rounded-lg border-2 transition-all ${
                              personaFormData.avatar_id === avatar.id
                                ? 'border-indigo-500 bg-indigo-50'
                                : 'border-border-primary hover:border-border-primary bg-white'
                            }`}
                          >
                            <img
                              src={avatar.imagePath}
                              alt={avatar.label}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Modal Buttons */}
              {viewingPersona && !isPersonaEditMode ? (
                // View mode - Edit and Close buttons
                <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                  {canEdit && (
                    <button
                      onClick={handlePersonaEditClick}
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:col-start-1 sm:text-sm"
                    >
                      Edit
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setIsPersonaModalOpen(false);
                      setViewingPersona(null);
                      setEditingPersona(null);
                      setPersonaFormData({
                        name: '',
                        role: '',
                        description: '',
                        goals: '',
                        pain_points: '',
                        avatar_id: 'avatar-1',
                        age_range: '',
                        gender: '',
                        location: ''
                      });
                      setPersonaError('');
                    }}
                    className={`w-full inline-flex justify-center rounded-md border border-border-primary shadow-sm px-4 py-2 bg-white text-base font-medium text-text-secondary hover:bg-background-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm ${canEdit ? 'sm:col-start-2' : ''}`}
                  >
                    Close
                  </button>
                </div>
              ) : (
                // Edit mode or Create mode - Save/Update and Cancel buttons
                <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                  <button
                    onClick={isPersonaEditMode ? handleUpdatePersona : handleCreatePersona}
                    disabled={!personaFormData.name.trim()}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:col-start-2 sm:text-sm disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    {isPersonaEditMode ? 'Save Changes' : 'Create Persona'}
                  </button>
                  <button
                    onClick={() => {
                      if (isPersonaEditMode) {
                        // Cancel editing, go back to view mode
                        handlePersonaEditCancel();
                      } else {
                        // Cancel create, close modal
                        setIsPersonaModalOpen(false);
                        setViewingPersona(null);
                        setEditingPersona(null);
                        setPersonaFormData({
                          name: '',
                          role: '',
                          description: '',
                          goals: '',
                          pain_points: '',
                          avatar_id: 'avatar-1',
                          age_range: '',
                          gender: '',
                          location: ''
                        });
                        setPersonaError('');
                      }
                    }}
                    className="mt-3 w-full inline-flex justify-center rounded-full border border-border-primary shadow-sm px-4 py-2 bg-surface-primary text-base font-medium text-text-secondary hover:bg-background-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default ProjectDetail;
